/* FOM · ponto de entrada do mapa vivo.

   Fluxo: sessao -> carrega o mapa do banco -> desenha -> muta pela API ->
   reflete. O navegador nao guarda dado de dominio em momento nenhum; toda
   verdade vem de um GET, e toda mudanca passa por uma rota real. */
import { criarApi, ApiError, ConflitoError } from './core/api.ts';
import { criarStore, noPorId, type Store } from './core/store.ts';
import { criarMapa, agregarSubarvores, type Mapa } from './render/map.ts';
import { compararIrmaos } from './core/layout.ts';
import { COR_ST, definirCatalogoTipos } from './core/types.ts';
import type { TipoDeNo } from './core/types.ts';
import { montarPainel, formularioNo, formularioRelacao, type DadosNo } from './ui/painel.ts';
import { montarCerebro } from './render/cerebro.ts';
import { montarPrevia } from './ui/previa.ts';
import { DIMENSOES, PRODUTOS, filtrarPerspectivaOrganizacao, projetarPerspectivaOrganizacao, type Dimensao } from './dominio/dimensoes.ts';
import { definirGuiaOperacional } from './dominio/guia-operacional.ts';
import { montarCommandCenter, type DadosCommandCenter } from './ui/command-center.ts';
import { montarProdutos } from './ui/produtos.ts';
import { montarConhecimento } from './ui/conhecimento.ts';
import { montarEvidencia } from './ui/evidencia.ts';
import { montarProcessos } from './render/processos.ts';
import type { BrainStatus, PublicationResumo, InvestigationResumo } from './core/types.ts';
import { montarTelaLogin } from './ui/sessao.ts';
import { montarGaveta, type Aba } from './ui/gaveta.ts';
import { montarPaleta, type Paleta } from './ui/paleta.ts';
import { LENTES, contarPorLente, ehFuturo, apenasFuturoEAncestrais, contagensDaVisao, type Lente } from './core/lentes.ts';
import { criarHistoricoLocal } from './core/desfazer.ts';
import type { FomNode, Relation } from './core/types.ts';

const api = criarApi();
const store = criarStore();

const raiz = document.getElementById('app')!;
let mapa: Mapa | null = null;
let painel: ReturnType<typeof montarPainel> | null = null;
let cerebro: ReturnType<typeof montarCerebro> | null = null;
let previa: ReturnType<typeof montarPrevia> | null = null;
/* MAP VIEW × BRAIN VIEW — dois modos da MESMA realidade (nunca duas bases) */
let modoVisao: 'mapa' | 'cerebro' = 'mapa';
/* perspectiva ativa: ORGANIZACAO filtra o LAYOUT (processos saem da
   leitura); as demais nao filtram. null = arvore fisica completa */
let perspectivaAtual: Dimensao | null = null;
/* papel do usuario nesta org — vem do servidor; a UI so EXPLICA a permissao */
let papelAtual: import('./core/types.ts').MemberRole = 'viewer';
let invsCerebro: import('./core/types.ts').InvestigationResumo[] | null = null;
/* A gaveta so existe depois que o mapa carrega: ela precisa saber a
   organizacao, o mapa e o PAPEL do usuario — e papel vem do servidor. */
let gaveta: ReturnType<typeof montarGaveta> | null = null;
let paleta: Paleta | null = null;
/* NEXT-GEN: superficies adicionais e a folha de evidencia */
let comando: ReturnType<typeof montarCommandCenter> | null = null;
let produtos: ReturnType<typeof montarProdutos> | null = null;
let conhecimento: ReturnType<typeof montarConhecimento> | null = null;
let processos: ReturnType<typeof montarProcessos> | null = null;
/* WORKSPACE ativo no palco (gate 0072): Organizacao = arvore com zoom
   semantico numa stagebox com cabecalho e cartoes; Processos = cena de
   trilhos; null = mapa neutro. */
type Workspace = 'organizacao' | 'processos' | null;
let wsAtual: Workspace = null;
/* ENTRADA PADRAO (gate 0070, convergencia com o artefato aprovado): o
   Command Center abre SOBRE a cena organizacional homologada — a cena
   continua preparada por baixo e a um Esc de distancia. */
const ENTRADA_PADRAO: 'comando' | 'organizacao' = 'comando';
let evidencia: ReturnType<typeof montarEvidencia> | null = null;
/* respostas que carregarKpis ja obtem; o Command Center le as mesmas */
let dadosCc: {
  invs: InvestigationResumo[] | null;
  brain: BrainStatus | null;
  pubs: PublicationResumo[] | null;
  hist: import('./core/types.ts').HistoryEntry[];
} = { invs: null, brain: null, pubs: null, hist: [] };
let usuarioNome = '';
let lenteAtual: Lente = 'completa';

/* P0-1 (Fase 1X.1): os numeros do cockpit derivam do conjunto que a visao
   renderiza — nunca dos totais fisicos do banco (que incluem ocultos). */
function contagensVisao() {
  return contagensDaVisao(store.estado.server.nos, store.estado.server.relacoes, lenteAtual);
}
let interagiuComMapa = false;
/* saltos de navegacao (busca, conexoes, breadcrumb) guardam de onde vieram */
const pilhaSelecao: string[] = [];
/* Ctrl+Z de verdade: cada mutacao registra a inversa, executada pela API real.
   A auditoria nunca encolhe — desfazer e uma operacao nova. */
const histLocal = criarHistoricoLocal();
let modoFocoAtual: 'sabido' | 'lacunas' | null = null;
/* dados dos KPIs — buscados junto com o mapa, nunca inventados */
let kpi: {
  invsAbertas: number | null;
  brainPendentes: number | null;
  brainAtivo: boolean;
  ultimaPub: { version: number; at: string } | null;
  hoje: { conhecimento: number; investigacoes: number };
  porDia: number[];
} = { invsAbertas: null, brainPendentes: null, brainAtivo: false, ultimaPub: null,
      hoje: { conhecimento: 0, investigacoes: 0 }, porDia: [] };
let orgSlug = '';
let mapSlug = '';

/* ------------------------------------------------------------------ chrome -- */
function montarAplicacao(): void {
  /* A tela de login deixa `tela-login` no #app (display: grid centrado). Sem
     limpar, a aplicacao INTEIRA nasce centrada e encolhida dentro desse grid —
     foi o layout espremido visto na homologacao. */
  raiz.className = '';
  /* ARQUITETURA DO CABECALHO — menos botoes, mais contexto.
     Esquerda: identidade + ONDE ESTOU (breadcrumb do no selecionado).
     Direita:  o PULSE (estado do conhecimento, abre o detalhe), a BUSCA
     (Ctrl+K — a porta unica), o Brain (a camada de inteligencia merece
     presenca propria) e um menu de camadas para o que se consulta as vezes.
     Controles de VISAO (zoom, enquadrar, filtros) moram sobre o proprio
     mapa: sao do mapa, nao do produto. */
  /* AUDITORIA DE INFORMACAO (Fase 1S.1) — cada numero tem UM lugar principal:
       contagens de conhecimento/lacunas -> grupo CONHECIMENTO da navegacao;
       visao geral do mapa -> overlay do proprio mapa (some ao interagir);
       atividade/publicacao/brain -> faixa de KPIs;
       fingerprint -> selo unico no header (confiar ou alertar).
     O header responde: onde estou, esta integro, como busco, quem sou. */
  raiz.innerHTML = `
    <div class="app app-com-sidebar">
      <aside class="sidebar" aria-label="Navegacao principal">
        <div class="sidebar-marca">
          <span class="sigla">FOM</span>
          <span class="sidebar-sub">Forwarder Operating Map</span>
        </div>
        <p class="sidebar-titulo">Comando</p>
        <nav class="sidebar-nav">
          <button class="sidebar-item sidebar-item-comando" id="nav-comando"
                  title="O que importa hoje — filas derivadas do que o mapa ja sabe">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
            <span>Command Center</span><b class="sidebar-conta" id="conta-atencao"></b></button>
        </nav>
        <p class="sidebar-titulo">Workspaces</p>
        <nav class="sidebar-nav">
          <button class="sidebar-item" id="nav-organizacao" title="Como a empresa se compoe">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4" width="18" height="5" rx="1.5"/><rect x="3" y="15" width="7" height="5" rx="1.5"/><rect x="14" y="15" width="7" height="5" rx="1.5"/><path d="M12 9v3M6.5 12v3M17.5 12v3M6.5 12h11"/></svg>
            <span>Organizacao</span></button>
          <button class="sidebar-item" id="nav-processos" title="Como o trabalho acontece">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/><path d="M7 12h3M14 12h3"/></svg>
            <span>Processos</span></button>
          <button class="sidebar-item" id="nav-produtos" title="IM · EM · IA · EA">
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="7" width="18" height="10" rx="5"/><path d="M8 12h8"/></svg>
            <span>Produtos</span><b class="sidebar-conta">4</b></button>
          <button class="sidebar-item" id="nav-conhecimento-ws" title="Sabemos · acreditamos · nao sabemos (lacunas) · planejamos">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" stroke-dasharray="3 3"/></svg>
            <span>Conhecimento</span><b class="sidebar-conta" id="conta-gaps" title="lacunas abertas"></b></button>
          <button class="sidebar-item" id="nav-brain" title="Operating Brain: observa e propoe; nada vira fato sem uma pessoa">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l3 3-3 3-3-3zM12 15l3 3-3 3-3-3z"/><path d="M12 9v6M5 12h14"/></svg>
            <span>Agente</span><b class="sidebar-conta" id="conta-brain"></b></button>
        </nav>
        <p class="sidebar-titulo">Conceituais</p>
        <nav class="sidebar-nav sidebar-conceituais" aria-label="Superficies conceituais, sem dado no FOM">
          <button class="sidebar-item" type="button" disabled title="Oportunidades pertencem ao FOE. O FOM fornece contexto operacional. Sem dado."><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 18 10 11l4 4 6-8"/><path d="M15 7h5v5"/></svg><span>Oportunidades</span><b class="sidebar-fut">futuro</b></button>
          <button class="sidebar-item" type="button" disabled title="Nao existe dado de rota, carrier, tarifa ou volume no FOM. Entraria por interface governada."><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17c6-10 12-10 18 0"/><circle cx="3" cy="17" r="1.5"/><circle cx="21" cy="17" r="1.5"/></svg><span>Trade Lanes</span><b class="sidebar-fut">futuro</b></button>
          <button class="sidebar-item" type="button" disabled title="Parcial: as regras de preco vivem no guia (E2) e na etapa 'Direcionamento ao Pricing'. Uma superficie propria e conceitual."><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 19V5M4 19h16"/><path d="M8 15l4-5 3 3 5-7"/></svg><span>Pricing</span><b class="sidebar-fut">parcial</b></button>
        </nav>
        <p class="sidebar-titulo">Governanca</p>
        <nav class="sidebar-nav">
          <button class="sidebar-item" id="nav-inv">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="6"/><path d="m20 20-4-4"/></svg>
            <span>Investigacoes</span><b class="sidebar-conta" id="conta-invs"></b></button>
          <button class="sidebar-item" id="nav-gov" title="Trilha (tudo o que aconteceu, em ordem) e publicacoes (retratos imutaveis)">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3 4 7v5c0 5 3.5 8 8 9 4.5-1 8-4 8-9V7z"/><path d="m9 12 2 2 4-4"/></svg>
            <span>Trilha · Publicacao</span></button>
          <button class="sidebar-item" id="nav-oc">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6z"/><path d="M4 4l16 16"/></svg>
            <span>Ocultos</span></button>
          <button class="sidebar-item" id="nav-mapa" title="O mapa neutro: a arvore fisica, verdade e proveniencia">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z"/><path d="M9 4v14M15 6v14"/></svg>
            <span>Mapa neutro</span></button>
        </nav>
        <div class="sidebar-espaco"></div>
        <button class="sidebar-tog" id="rail-tog" type="button" aria-pressed="false" title="Recolher a navegacao para icones">‹ recolher</button>
        <div class="sidebar-rodape">
          <button class="sidebar-item" id="recarregar" title="A verdade e o banco" hidden>Recarregar</button>
          <button class="sidebar-item" id="sair">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9"/></svg>
            <span>Sair</span></button>
        </div>
      </aside>
      <div class="coluna-principal">
        <header class="topo">
          <div class="topo-marca"><span class="org" id="org"></span></div>
          <nav class="topo-crumbs" id="topo-crumbs" aria-label="Contexto atual"></nav>
          <button class="selo-base" id="selo-base" title="Integridade da BASE congelada"></button>
          <div class="topo-acoes">
            <button class="botao botao-buscar" id="abrir-paleta-topo" title="Buscar e executar">
              <span aria-hidden="true">⌕</span> Pesquisar <kbd>Ctrl K</kbd>
            </button>
            <button class="botao botao-comando-topo" id="abrir-comando-topo" title="O que importa hoje">O que importa</button>
            <div class="usuario-chip" id="usuario-chip" hidden>
              <span class="usuario-avatar" aria-hidden="true"></span>
              <span class="usuario-nome" id="usuario-nome"></span>
              <span class="usuario-papel" id="usuario-papel"></span>
            </div>
          </div>
        </header>
        <main class="corpo">
          <section class="palco-host" id="palco">
            <div class="contexto-mapa">
              <div class="grupo-nav" id="grupo-perspectiva">
                <span class="grupo-rotulo">Perspectiva</span>
                <div class="dimensoes" id="dimensoes" role="group" aria-label="Perspectiva da exploracao"></div>
              </div>
              <span class="aviso-perspectiva" id="aviso-perspectiva" hidden></span>
              <div class="grupo-nav" id="grupo-estado">
                <span class="grupo-rotulo">Estado</span>
                <div class="lentes" id="lentes" role="group" aria-label="Estado do mapa"></div>
                <button class="lentes-info" id="lentes-info" aria-haspopup="dialog" title="o que cada estado significa">?</button>
                <div class="lentes-popover" id="lentes-popover" role="dialog" aria-label="O que cada estado significa" hidden></div>
              </div>
              <div class="grupo-nav grupo-nivel" id="grupo-nivel">
                <span class="grupo-rotulo">Nivel</span>
                <div class="seg" id="nivel-seg" role="group" aria-label="Nivel semantico do mapa: capsulas, grupos ou nos">
                  <button type="button" data-nivel="capsulas" title="Areas como capsulas agregadas: descendentes, conhecimento, lacunas e distribuicao epistemica">Capsulas</button>
                  <button type="button" data-nivel="grupos" title="Areas abertas; funcoes e processos como capsulas">Grupos</button>
                  <button type="button" data-nivel="nos" title="Tres niveis abertos; cartoes completos">Nos</button>
                </div>
              </div>
              <div class="grupo-nav grupo-camadas" id="grupo-camadas">
                <span class="grupo-rotulo">Camadas</span>
                <label class="alternar-relacoes camada-toggle">
                  <input type="checkbox" id="ver-relacoes" checked />
                  <span>relacoes</span>
                </label>
                <button type="button" class="camada-toggle sidebar-filtro" id="nav-conhecimento" aria-pressed="false" title="Realca no mapa o que ja esta consolidado">conhecimento</button>
                <button type="button" class="camada-toggle sidebar-filtro" id="nav-lacunas" aria-pressed="false" title="Realca no mapa o que ainda falta descobrir">lacunas</button>
              </div>
              <div class="grupo-nav grupo-origem" id="grupo-origem">
                <span class="grupo-rotulo grupo-rotulo-origem">Origem</span>
                <div class="filtros" id="filtros"></div>
              </div>
              <nav class="pao" id="pao" aria-label="Caminho do no selecionado"></nav>
            </div>
            <div class="visao-geral" id="visao-geral" hidden>
              <p class="visao-geral-titulo">Mapa vivo da operacao</p>
              <p class="visao-geral-sub">Estrutura operacional da organizacao</p>
              <div class="visao-geral-numeros" id="visao-geral-numeros"></div>
            </div>
            <header class="ws-head" id="ws-head" hidden></header>
            <div class="stagebox" id="stagebox">
              <div class="scene-title" id="scene-title" hidden></div>
              <div class="scene-legend" id="scene-legend" hidden></div>
            </div>
            <section class="ws-lista cardx" id="ws-lista" hidden aria-label="Estrutura como lista"></section>
            <div class="ws-cards cols c3" id="ws-cards" hidden></div>
            <div class="toolbar-mapa" role="toolbar" aria-label="Controles do mapa">
              <button class="botao" id="zoom-menos" title="Afastar (alem do piso legivel, recolhe um nivel)">−</button>
              <span class="sidebar-zoom-nivel" id="zoom-nivel">100%</span>
              <button class="botao" id="zoom-mais" title="Aproximar">+</button>
              <span class="toolbar-lod" id="toolbar-lod" title="nivel semantico atual"></span>
              <button class="botao botao-fantasma" id="ajustar">Enquadrar</button>
            </div>
          </section>
          <aside class="lateral" id="lateral"></aside>
          <aside id="gaveta" hidden></aside>
        </main>
        <footer class="kpis" id="kpis" hidden></footer>
      </div>
      <div class="aviso" id="aviso" hidden></div>
      <div class="dialogo-host" id="dialogo" hidden></div>
      <div id="paleta" hidden></div>
      <div class="pulse-popover" id="pulse-popover" hidden>
        <p class="pulse-titulo">Integridade da BASE congelada</p>
        <p class="pulse-nota" id="pulse-detalhe"></p>
      </div>
      <footer class="rodape" id="rodape" hidden></footer>
    </div>
  `;

  const palco = document.getElementById('palco')!;
  const lateral = document.getElementById('lateral')!;
  const dialogo = document.getElementById('dialogo')!;
  /* STAGEBOX (gate 0072, artefato): o mapa vive numa caixa de cena propria
     dentro do palco — cabecalho do workspace acima, cartoes abaixo, titulo e
     legenda da cena como sobreposicoes. A toolbar e o minimapa moram nela. */
  const stagebox = document.getElementById('stagebox') as HTMLElement;
  const barra = palco.querySelector('.toolbar-mapa')!;
  const contexto = palco.querySelector('.contexto-mapa')!;
  const sceneTitle = stagebox.querySelector('.scene-title')!;
  const sceneLegend = stagebox.querySelector('.scene-legend')!;

  mapa = criarMapa(stagebox, {
    aoSelecionar: (id) => {
      if (store.estado.ui.ligando && id && id !== store.estado.ui.ligando) return;
      store.selecionar(id);
      if (id) revelarFilhos(id);
      /* selecionar e ENTRAR: a camera aproxima se o usuario esta longe ou o
         no esta na borda — nunca corta, sempre transiciona (map.ts decide) */
      if (id && modoVisao === 'mapa') mapa?.focarEm(id);
    },
    /* EXPANSAO LOCAL (gate 0072): o no clicado fica ANCORADO no mesmo ponto
       da tela enquanto o ramo se recompoe em volta dele; depois a camera faz
       o menor movimento que traz pai + filhos visiveis para a viewport.
       "Abri este ramo" — nunca "o mapa inteiro explodiu". */
    aoAlternarExpansao: (id) => {
      const ancora = mapa?.posicaoNaTela(id) ?? null;
      const abrindo = !store.estado.ui.expandidos.has(id);
      store.alternarExpandido(id);
      mapa?.ancorarEm(id, ancora);
      if (abrindo) requestAnimationFrame(() => mapa?.enquadrarRamo(id));
    },
    aoSelecionarRelacao: (id) => {
      store.selecionarRelacao(id);
    },
    aoLigar: (de, para) => void conectar(de, para),
    aoMoverNo: (id, dx, dy) => void moverNo(id, dx, dy),
    aoZoom: (z) => {
      const n = document.getElementById('zoom-nivel');
      if (n) n.textContent = Math.round(z * 100) + '%';
    },
    /* ZOOM SEMANTICO: afastar alem do piso legivel recolhe UM nivel (nos →
       grupos → capsulas) e reapresenta legivel — a representacao muda, a
       escala nao encolhe ate o ilegivel. Ja em capsulas, o zoom segue livre. */
    aoZoomSemantico: (direcao) => {
      if (direcao !== 'afastar') return false;
      const alvo = proximoNivelMenor();
      if (!alvo) return false;
      definirNivel(alvo);
      return true;
    },
  });
  /* criarMapa limpa o host (a stagebox): as sobreposicoes da cena voltam */
  stagebox.appendChild(sceneTitle);
  stagebox.appendChild(sceneLegend);
  stagebox.appendChild(barra);
  void contexto;
  /* cena de PROCESSOS (artefato): trilhos com estacoes, na stagebox */
  processos = montarProcessos(stagebox, {
    aoSelecionar: (id) => { store.expandirCaminho(id); store.selecionar(id); },
  });

  /* NEXT-GEN: superficies ADICIONAIS sobre o palco. A entrada padrao do
     produto continua sendo a cena organizacional homologada; o Command
     Center e Produtos sao portas alternativas, e o Evidence Inspector e uma
     folha sobre qualquer objeto. Nada aqui inventa dado. */
  /* a barra de contexto agora e uma LINHA DE FLUXO acima do mapa (nunca
     sobre o painel); o cerebro, que e absoluto, comeca abaixo dela */
  const medirContexto = (): void => {
    (palco as HTMLElement).style.setProperty('--ctx-altura', `${(contexto as HTMLElement).offsetHeight}px`);
  };
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(medirContexto).observe(contexto);
  medirContexto();

  const comandoEl = document.createElement('section');
  comandoEl.className = 'superficie';
  comandoEl.id = 'command-center';
  palco.appendChild(comandoEl);
  comando = montarCommandCenter(comandoEl, {
    aoIrParaNo: (id) => { fecharSuperficies(); irParaNo(id); },
    aoInvestigarLacuna: (no, pergunta) => { fecharSuperficies(); irParaNo(no.external_id); void abrirLacuna(no, pergunta); },
    aoAbrirCamada: (aba, no) => { fecharSuperficies(); void abrirCamada(aba, no); },
    aoEntrarOrganizacao: () => { fecharSuperficies(); perspectivaAtual = 'organizacao'; void entrarNaOrganizacao(); pintarPerspectiva(); },
    aoEntrarProdutos: () => abrirProdutos(),
    aoFechar: () => fecharSuperficies(),
  });
  const produtosEl = document.createElement('section');
  produtosEl.className = 'superficie';
  produtosEl.id = 'superficie-produtos';
  palco.appendChild(produtosEl);
  produtos = montarProdutos(produtosEl, {
    aoEntrar: (id) => { fecharSuperficies(); perspectivaAtual = 'produtos'; irParaNo(id); void entrarNoCerebro(); pintarPerspectiva(); },
    aoIrParaNo: (id) => { fecharSuperficies(); irParaNo(id); },
    aoFechar: () => fecharSuperficies(),
  });
  const conhecimentoEl = document.createElement('section');
  conhecimentoEl.className = 'superficie';
  conhecimentoEl.id = 'superficie-conhecimento';
  palco.appendChild(conhecimentoEl);
  conhecimento = montarConhecimento(conhecimentoEl, {
    aoIrParaNo: (id) => { fecharSuperficies(); irParaNo(id); },
    aoInvestigarLacuna: (no, lacuna) => { fecharSuperficies(); irParaNo(no.external_id); void abrirLacuna(no, lacuna); },
    aoAbrirEvidencia: (no) => void evidencia?.abrir(no, store.estado.server.nos),
    papel: () => papelAtual,
    aoFechar: () => fecharSuperficies(),
  });
  evidencia = montarEvidencia(document.body, {
    api, org: () => orgSlug, mapa: () => mapSlug, papel: () => papelAtual,
    aoInvestigarLacuna: (no, lacuna) => { irParaNo(no.external_id); void abrirLacuna(no, lacuna); },
    aoAbrirInvestigacoes: (no) => void abrirCamada('investigacao', no),
    aoIrParaNo: (id) => irParaNo(id),
  });

  /* SKELETON do primeiro carregamento (1AD.1): estrutura aproximada, sem
     numero ou dado ficticio; some no primeiro pintar com dados reais. */
  const esqueleto = document.createElement('div');
  esqueleto.id = 'skeleton-palco';
  esqueleto.className = 'skeleton-palco';
  esqueleto.setAttribute('aria-hidden', 'true');
  esqueleto.innerHTML = '<div class="sk-bloco sk-topo"></div>'
    + '<div class="sk-bloco sk-no sk-no-1"></div>'
    + '<div class="sk-bloco sk-no sk-no-2"></div>'
    + '<div class="sk-bloco sk-no sk-no-3"></div>';
  palco.appendChild(esqueleto);

  /* PREVIA de no (hover, ponteiro fino) + PARALLAX sutil (1AD.2) */
  previa = montarPrevia(document.body);
  const ponteiroFino = typeof matchMedia !== 'undefined' && matchMedia('(pointer: fine)').matches;
  const menosMovimento = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (ponteiroFino) {
    let previaTimer: ReturnType<typeof setTimeout> | null = null;
    palco.addEventListener('pointerover', (ev) => {
      if ((ev as PointerEvent).pointerType === 'touch') return;
      const alvo = (ev.target as Element).closest('.no') as SVGGElement | null;
      if (!alvo) return;
      const id = alvo.dataset.id;
      if (!id) return;
      if (previaTimer) clearTimeout(previaTimer);
      previaTimer = setTimeout(() => {
        const no = noPorId(store.estado, id);
        if (no) previa?.mostrar(no, (ev as PointerEvent).clientX, (ev as PointerEvent).clientY);
      }, 220);
    });
    palco.addEventListener('pointerout', (ev) => {
      const de = (ev.target as Element).closest('.no');
      if (!de) return;
      if (previaTimer) clearTimeout(previaTimer);
      previa?.esconder();
    });
    palco.addEventListener('pointerdown', () => {
      if (previaTimer) clearTimeout(previaTimer);
      previa?.esconder();
    });
    /* breadcrumb tambem preve o contexto do nivel */
    document.getElementById('pao')?.addEventListener('pointerover', (ev) => {
      const item = (ev.target as Element).closest('[data-previa-id]') as HTMLElement | null;
      if (!item) return;
      const no = noPorId(store.estado, item.dataset.previaId!);
      if (no) previa?.mostrar(no, (ev as PointerEvent).clientX, (ev as PointerEvent).clientY);
    });
    document.getElementById('pao')?.addEventListener('pointerout', () => previa?.esconder());
  }
  if (ponteiroFino && !menosMovimento) {
    /* parallax minimo do fundo: informacao NUNCA se move — so a textura */
    let parPendente = false;
    palco.addEventListener('pointermove', (ev) => {
      if (parPendente) return;
      parPendente = true;
      requestAnimationFrame(() => {
        parPendente = false;
        const r = palco.getBoundingClientRect();
        const px = (((ev as PointerEvent).clientX - r.left) / Math.max(r.width, 1) - 0.5) * 6;
        const py = (((ev as PointerEvent).clientY - r.top) / Math.max(r.height, 1) - 0.5) * 6;
        (palco as HTMLElement).style.setProperty('--plx', px.toFixed(1));
        (palco as HTMLElement).style.setProperty('--ply', py.toFixed(1));
      });
    });
  }

  /* BRAIN VIEW — mesma realidade, outra lente. Montado sobre o palco. */
  cerebro = montarCerebro(palco as HTMLElement, {
    aoSelecionar: (id) => {
      store.expandirCaminho(id);
      store.selecionar(id);
    },
    aoAbrirAba: (aba) => painel?.abrirAba(aba),
    aoVoltarAoMapa: () => sairDoCerebro(),
    aoInvestigarLacuna: (no, pergunta) => void abrirLacuna(no, pergunta),
  });
  montarModos();
  montarDimensoes();
  montarInfoLentes();

  document.getElementById('ver-relacoes')!.addEventListener('change', (ev) => {
    mapa?.mostrarRelacoes((ev.target as HTMLInputElement).checked);
  });
  montarLentes();

  /* Ctrl+Z / Ctrl+Shift+Z — fora de campos de texto e com a paleta fechada */
  document.addEventListener('keydown', (ev) => {
    if (!(ev.ctrlKey || ev.metaKey) || ev.key.toLowerCase() !== 'z') return;
    const alvo = ev.target as HTMLElement;
    if (alvo.closest('input, textarea, select, [contenteditable]')) return;
    if (paleta?.aberta) return;
    ev.preventDefault();
    if (ev.shiftKey) void executarRefazer();
    else void executarDesfazer();
  });

  painel = montarPainel(lateral, {
    papel: () => papelAtual,
    aoCriarFilho: (pai) => {
      formularioNo(dialogo, `Novo no em "${pai.name}"`, { st: 'ND' },
        (dados) => void criarNo(pai, dados), opcoesDeTipo());
    },
    aoEditar: (no) => {
      formularioNo(dialogo, 'Editar no', {
        name: no.name,
        st: no.st,
        node_type: no.node_type ?? undefined,
        summary: no.summary ?? undefined,
        src: no.src ?? undefined,
      }, (dados) => void editarNo(no, dados), opcoesDeTipo());
    },
    aoRemover: (no) => void removerNo(no),
    aoLigarDe: (no) => {
      store.patchUi({ ligando: no.external_id });
      store.mensagem('aviso', `Conectando a partir de "${no.name}". Clique no no de destino.`);
    },
    /* GATE 0062: corrigibilidade das relacoes — expoe PATCH/DELETE que o
       backend ja tinha (FOM-ARQ-0061). So em linhas de relacao semantica. */
    aoEditarRelacao: (rel) => editarRelacao(rel),
    aoRemoverRelacao: (rel) => confirmarRemoverRelacao(rel),
    /* salto = navegacao: passa pela pilha para o 'voltar' existir */
    aoCentralizar: (id) => irParaNo(id),
    aoInvestigarLacuna: (no, lacuna) => void abrirLacuna(no, lacuna),
    aoAbrirContrato: (no) => void abrirCamada('contrato', no),
    aoAbrirInvestigacoes: (no) => void abrirCamada('investigacao', no),
    aoAbrirEvidencia: (no) => void evidencia?.abrir(no, store.estado.server.nos),
    resumoVazio: construirResumoVazio,
    investigacoesDe: () => api.investigacoes(orgSlug, mapSlug),
  });

  paleta = montarPaleta(document.getElementById('paleta')!, {
    nos: () => store.estado.server.nos,
    acoes: () => [
      { id: 'comando', rotulo: 'Command Center — o que importa hoje', dica: 'filas derivadas do que o mapa ja sabe', executar: () => abrirComando() },
      { id: 'evidencia', rotulo: 'Evidence Inspector do no selecionado', dica: 'lacuna → pergunta → evidencia → resposta → classificacao → conhecimento', executar: () => { const n = selecionadoAtual(); if (n) void evidencia?.abrir(n, store.estado.server.nos); else store.mensagem('aviso', 'Selecione um no primeiro.'); } },
      { id: 'produtos', rotulo: 'Produtos — IM · EM · IA · EA', dica: 'produto → fluxo → operacao → parceiros', executar: () => abrirProdutos() },
      { id: 'conhecimento', rotulo: 'Conhecimento — sabemos · acreditamos · nao sabemos · planejamos', dica: 'camada de conhecimento operacional', executar: () => abrirConhecimento('sabemos') },
      { id: 'lacunas', rotulo: 'Lacunas — o que ainda nao sabemos', dica: 'workspace Conhecimento, foco em Nao sabemos', executar: () => abrirConhecimento('lacunas') },
      { id: 'processos', rotulo: 'Processos — como o trabalho acontece', dica: 'estacoes na ordem real do fluxo', executar: () => { fecharSuperficies(); abrirProcessos(); } },
      { id: 'organizacao', rotulo: 'Organizacao — cena organizacional', dica: 'a porta de entrada homologada', executar: () => { fecharSuperficies(); perspectivaAtual = 'organizacao'; void entrarNaOrganizacao(); pintarPerspectiva(); } },
      { id: 'nivel-capsulas', rotulo: 'Nivel: capsulas (areas agregadas)', dica: 'zoom semantico', executar: () => definirNivel('capsulas') },
      { id: 'nivel-nos', rotulo: 'Nivel: nos (tres niveis abertos)', dica: 'zoom semantico', executar: () => definirNivel('nos') },
      { id: 'enquadrar', rotulo: 'Enquadrar o mapa inteiro', dica: 'visao geral', executar: () => mapa?.ajustar() },
      { id: 'brain', rotulo: 'Abrir o Operating Brain', dica: 'observa e propoe', executar: () => void abrirCamada('brain', selecionadoAtual()) },
      { id: 'inv', rotulo: 'Abrir investigacoes', dica: 'perguntas abertas', executar: () => void abrirCamada('investigacao', selecionadoAtual()) },
      { id: 'pub', rotulo: 'Abrir publicacoes', dica: 'retratos imutaveis do mapa', executar: () => void abrirCamada('publicacao', null) },
      { id: 'tri', rotulo: 'Abrir a trilha', dica: 'tudo o que aconteceu, em ordem', executar: () => void abrirCamada('trilha', null) },
      { id: 'ocultos', rotulo: 'Ver nos ocultos', dica: 'removidos logicamente — nada se destroi', executar: () => void abrirCamada('ocultos', null) },
      { id: 'recarregar', rotulo: 'Recarregar do banco', dica: 'a verdade e o banco', executar: () => void carregarMapa(true) },
      { id: 'guia', rotulo: 'Como o FOM funciona (guia)', dica: 'o ciclo em 8 passos', executar: abrirGuia },
      { id: 'desfazer', rotulo: 'Desfazer ultima acao', dica: 'Ctrl+Z — a trilha nunca encolhe', executar: () => void executarDesfazer() },
      { id: 'refazer', rotulo: 'Refazer', dica: 'Ctrl+Shift+Z', executar: () => void executarRefazer() },
      { id: 'f-base', rotulo: 'Mostrar/ocultar camada BASE', executar: () => alternarFiltro('base') },
      { id: 'f-user', rotulo: 'Mostrar/ocultar camada USER', executar: () => alternarFiltro('user') },
    ],
    investigacoes: () => api.investigacoes(orgSlug, mapSlug),
    aoIrParaNo: irParaNo,
    aoInvestigarLacuna: (no, lacuna) => {
      irParaNo(no.external_id);
      void abrirLacuna(no, lacuna);
    },
    aoAbrirInvestigacao: (inv) => {
      irParaNo(inv.node_external_id);
      const no = noPorId(store.estado, inv.node_external_id) ?? null;
      void abrirCamada('investigacao', no);
    },
  });

  document.addEventListener('keydown', (ev) => {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault();
      paleta?.alternar();
    }
  });
  document.getElementById('abrir-paleta-topo')!.addEventListener('click', () => paleta?.abrir());

  const nav = (id: string, acao: () => void): void => {
    document.getElementById(id)!.addEventListener('click', acao);
  };
  nav('nav-mapa', () => { fecharSuperficies(); sairDoCerebro(); gaveta?.fechar(); definirWorkspace(null); perspectivaAtual = null; pintarPerspectiva(); store.selecionar(null); pintarNav(); });
  nav('nav-comando', () => abrirComando());
  nav('nav-organizacao', () => { fecharSuperficies(); perspectivaAtual = 'organizacao'; void entrarNaOrganizacao(); pintarPerspectiva(); pintarNav(); });
  nav('nav-processos', () => { fecharSuperficies(); abrirProcessos(); pintarNav(); });
  nav('nav-produtos', () => abrirProdutos());
  nav('nav-conhecimento-ws', () => abrirConhecimento('sabemos'));
  nav('nav-gov', () => void abrirCamada('trilha', null));
  /* rail recolhivel (artefato): so icones; o toggle diz o que faz */
  /* SPATIAL v3 · RAIL DE BAIXA DOMINANCIA. O mecanismo de recolher ja existia;
     o que faltava era politica. Acima de 1180px o rail NASCE recolhido, porque
     o territorio e o assunto e a navegacao e instrumento. Abaixo de 1181px o
     produto ja recolhia sozinho por media query, e nada muda.
     A escolha do usuario e lembrada por navegador (preferencia local, nunca
     dado do FOM); enquanto nao houver escolha, vale o padrao por largura.
     Nada foi removido do rail: os onze itens continuam presentes, alcancaveis
     por teclado e pela paleta Ctrl+K. */
  const CHAVE_RAIL = 'fom.rail.recolhido';
  const lerPrefRail = (): boolean | null => {
    try {
      const v = localStorage.getItem(CHAVE_RAIL);
      return v === null ? null : v === '1';
    } catch { return null; }
  };
  const aplicarRail = (min: boolean): void => {
    const app = document.querySelector('.app') as HTMLElement | null;
    const t = document.getElementById('rail-tog');
    if (!app || !t) return;
    app.classList.toggle('rail-min', min);
    t.textContent = min ? '›' : '‹ recolher';
    t.setAttribute('aria-pressed', String(min));
    t.title = min ? 'Expandir a navegacao' : 'Recolher a navegacao para icones';
  };
  document.getElementById('rail-tog')!.addEventListener('click', () => {
    const app = document.querySelector('.app') as HTMLElement;
    const min = !app.classList.contains('rail-min');
    aplicarRail(min);
    try { localStorage.setItem(CHAVE_RAIL, min ? '1' : '0'); } catch { /* sem armazenamento: vale a sessao */ }
  });
  /* ACESSIBILIDADE: recolhido, o rotulo visual some. O nome acessivel passa a
     vir do proprio rotulo, uma vez, no boot — nunca de texto inventado. */
  for (const item of Array.from(document.querySelectorAll<HTMLElement>('.sidebar-item'))) {
    if (item.getAttribute('aria-label')) continue;
    /* o rotulo visivel vem do <span>; quando o item nao tem span (o botao de
       recarregar, que nasce oculto), vale o proprio texto — nunca texto novo */
    const rotulo = (item.querySelector('span')?.textContent ?? item.textContent ?? '').trim();
    if (rotulo) item.setAttribute('aria-label', rotulo);
  }
  aplicarRail(lerPrefRail() ?? window.innerWidth > 1180);
  document.getElementById('abrir-comando-topo')!.addEventListener('click', () => abrirComando());
  for (const b of Array.from(document.querySelectorAll<HTMLButtonElement>('#nivel-seg [data-nivel]'))) {
    b.addEventListener('click', () => definirNivel(b.dataset.nivel as NivelSemantico));
  }
  nav('nav-inv', () => void abrirCamada('investigacao', selecionadoAtual()));
  nav('nav-brain', () => void abrirCamada('brain', selecionadoAtual()));
  nav('nav-oc', () => void abrirCamada('ocultos', null));
  /* as lentes de realce: Conhecimento acende o consolidado, Lacunas o que
     falta — no MAPA, que e onde a decisao acontece */
  nav('nav-conhecimento', () => {
    modoFocoAtual = modoFocoAtual === 'sabido' ? null : 'sabido';
    mapa?.modoFoco(modoFocoAtual);
    pintarSidebar();
  });
  nav('nav-lacunas', () => {
    modoFocoAtual = modoFocoAtual === 'lacunas' ? null : 'lacunas';
    mapa?.modoFoco(modoFocoAtual);
    pintarSidebar();
  });

  /* o mapa e vivo: a visao geral se reduz quando a pessoa comeca a operar */
  const marcarInteracao = (): void => {
    if (interagiuComMapa) return;
    interagiuComMapa = true;
    pintarVisaoGeral();
  };
  palco.addEventListener('pointerdown', marcarInteracao);
  palco.addEventListener('wheel', marcarInteracao, { passive: true });

  document.getElementById('selo-base')!.addEventListener('click', (ev) => {
    ev.stopPropagation();
    const pop = document.getElementById('pulse-popover')!;
    pop.hidden = !pop.hidden;
  });

  document.addEventListener('click', (ev) => {
    const pop = document.getElementById('pulse-popover');
    if (pop && !pop.hidden && !(ev.target as Element).closest('#selo-base, .pulse-popover')) {
      pop.hidden = true;
    }
  });
  document.getElementById('ajustar')!.addEventListener('click', () => mapa?.ajustar());
  document.getElementById('zoom-mais')!.addEventListener('click', () => mapa?.zoomPor(1.25));
  document.getElementById('zoom-menos')!.addEventListener('click', () => mapa?.zoomPor(1 / 1.25));
  document.getElementById('recarregar')!.addEventListener('click', () => void carregarMapa(true));
  document.getElementById('sair')!.addEventListener('click', () => void sair());

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape') {
      /* CORRECAO 0081 · a paleta ja fecha a si mesma no proprio keydown e nao
         para a propagacao: sem esta guarda, UM Esc fechava a paleta E derrubava
         a perspectiva Conhecimento junto. Mesma checagem que o Ctrl+Z ja faz. */
      if (paleta?.aberta) return;
      if (evidencia?.aberta) {
        evidencia.fechar();
      } else if (comando?.aberta || produtos?.aberta) {
        fecharSuperficies();
      } else if (gaveta?.aberta) {
        gaveta.fechar();
      } else if (modoVisao === 'cerebro') {
        sairDoCerebro();
      } else if (store.estado.ui.ligando) {
        store.patchUi({ ligando: null });
        store.mensagem('aviso', 'Conexao cancelada.');
      } else if (store.estado.ui.relacaoSelecionada) {
        /* sai da conexao e volta a uma das pontas (contexto nunca se perde) */
        const rel = store.estado.server.relacoes.find((r) => r.id === store.estado.ui.relacaoSelecionada);
        store.selecionarRelacao(null);
        if (rel) store.selecionar(rel.from);
      } else {
        store.selecionar(null);
      }
    }
  });

  montarFiltros();
  store.inscrever(pintar);
}

/* Filtros rapidos da sidebar: contagens REAIS + duas lentes visuais.
   BASE/USER continuam sendo os filtros de dados que ja existiam; Sabido e
   Lacunas sao LENTES (dimerizam o resto) — nada sai do DOM, nada re-renderiza. */
/* sidebar: contagens e estados — o lugar PRINCIPAL desses numeros */
function pintarSidebar(): void {
  const v = contagensVisao();
  const poe = (id: string, valor: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = valor;
  };
  poe('conta-know', String(v.conhecimentos));
  /* rail (artefato): Conhecimento carrega a contagem de LACUNAS abertas */
  poe('conta-gaps', String(v.lacunas));
  poe('conta-invs', kpi.invsAbertas === null ? '' : String(kpi.invsAbertas));
  poe('conta-brain', !kpi.brainAtivo ? 'inativo' : String(kpi.brainPendentes ?? 0));
  const bK = document.getElementById('nav-conhecimento');
  const bL = document.getElementById('nav-lacunas');
  bK?.classList.toggle('sidebar-filtro-ativo', modoFocoAtual === 'sabido');
  bK?.setAttribute('aria-pressed', String(modoFocoAtual === 'sabido'));
  bL?.classList.toggle('sidebar-filtro-ativo', modoFocoAtual === 'lacunas');
  bL?.setAttribute('aria-pressed', String(modoFocoAtual === 'lacunas'));
}

/* a visao geral VIVE NO MAPA e cede o palco quando a operacao comeca */
function pintarVisaoGeral(): void {
  const host = document.getElementById('visao-geral');
  if (!host) return;
  const e = store.estado;
  const c = e.server.contagens;
  const mostrar = !e.ui.selecionado && !interagiuComMapa && !!c;
  host.hidden = !mostrar;
  if (!mostrar || !c) return;
  const numeros = document.getElementById('visao-geral-numeros')!;
  const v = contagensVisao();
  numeros.innerHTML = `
    <span><b>${v.elementos}</b> elementos · <b>${v.relacoes}</b> relacoes</span>
    <span class="vg-know"><b>${v.conhecimentos}</b> conhecimentos consolidados</span>
    <span class="vg-gap"><b>${v.lacunas}</b> lacunas abertas</span>
    <span class="vg-inv">${kpi.invsAbertas === null ? '' : '<b>' + kpi.invsAbertas + '</b> investigacoes em andamento'}</span>`;
}

function montarFiltros(): void {
  const host = document.getElementById('filtros')!;
  host.innerHTML = '';
  for (const [rotulo, chave] of [['BASE', 'base'], ['USER', 'user']] as const) {
    const b = document.createElement('button');
    b.className = 'pilula-filtro ativa';
    b.textContent = rotulo;
    b.dataset.origem = chave;
    b.addEventListener('click', () => {
      const ui = store.estado.ui;
      const novo = chave === 'base' ? !ui.mostrarBase : !ui.mostrarUser;
      store.patchUi(chave === 'base' ? { mostrarBase: novo } : { mostrarUser: novo });
      b.classList.toggle('ativa', novo);
    });
    host.appendChild(b);
  }
}

/* selecionar e ENTRAR (homologacao executiva): um contexto selecionado
   REVELA seus filhos diretos — sem isso, entrar em Operacao deixava os
   quatro produtos invisiveis (achado da auditoria). So abre, nunca fecha. */
function revelarFilhos(externalId: string): void {
  for (const n of store.estado.server.nos) {
    if (n.parent_external_id === externalId) store.expandirCaminho(n.external_id);
  }
}

/* ir para um no: abre o caminho, seleciona e centraliza — a mesma coisa que
   os resultados da busca, o breadcrumb e as relacoes do painel fazem */
function irParaNo(externalId: string): void {
  if (perspectivaAtual === 'organizacao') {
    const { visiveis } = filtrarPerspectivaOrganizacao(store.estado.server.nos);
    if (!visiveis.some((n) => n.external_id === externalId)) {
      perspectivaAtual = null;
      definirWorkspace(null);
      pintarPerspectiva();
    }
  }
  /* na cena de processos, um alvo fora da subarvore de processos leva ao
     mapa neutro — o contexto nunca some em silencio */
  if (wsAtual === 'processos') {
    const raizProc = DIMENSOES.find((x) => x.id === 'processos')?.raiz ?? '';
    let cursor = noPorId(store.estado, externalId);
    let dentro = false;
    let guarda = 0;
    while (cursor && guarda++ < 64) { if (cursor.external_id === raizProc) { dentro = true; break; } cursor = cursor.parent_external_id ? noPorId(store.estado, cursor.parent_external_id) : undefined; }
    if (!dentro) { perspectivaAtual = null; definirWorkspace(null); pintarPerspectiva(); }
  }
  const atual = store.estado.ui.selecionado;
  if (atual && atual !== externalId) {
    pilhaSelecao.push(atual);
    if (pilhaSelecao.length > 12) pilhaSelecao.shift();
  }
  store.expandirCaminho(externalId);
  revelarFilhos(externalId);
  store.selecionar(externalId);
  /* a camera ENTRA (aproxima ao piso legivel), nao apenas centraliza */
  requestAnimationFrame(() => mapa?.focarEm(externalId));
}

function voltarSelecao(): void {
  const anterior = pilhaSelecao.pop();
  if (!anterior) return;
  store.expandirCaminho(anterior);
  store.selecionar(anterior);
  requestAnimationFrame(() => mapa?.centralizarEm(anterior));
}

function alternarFiltro(chave: 'base' | 'user'): void {
  const ui = store.estado.ui;
  const novo = chave === 'base' ? !ui.mostrarBase : !ui.mostrarUser;
  store.patchUi(chave === 'base' ? { mostrarBase: novo } : { mostrarUser: novo });
  const b = document.querySelector(`.pilula-filtro[data-origem="${chave}"]`);
  b?.classList.toggle('ativa', novo);
}

/* O painel quando NADA esta selecionado: orientacao + estado, nao uma frase
   perdida. E o "por onde comeco?" respondido na propria tela. */
function construirResumoVazio(): HTMLElement {
  /* CURTO de proposito: a visao geral do mapa mora NO mapa. Aqui e so o
     "o que fazer daqui" — §17: explicar sem virar parede de texto. */
  const box = document.createElement('div');
  box.className = 'vazio-guia';
  box.innerHTML = `
    <p class="vazio-titulo">Nenhum no selecionado</p>
    <p class="vazio-sub">Selecione um no para explorar conhecimento, lacunas, investigacoes e conexoes.</p>
    <ul class="vazio-dicas">
      <li><span class="ind-mini ind-mini-know" aria-hidden="true"></span> disco: conhecimento consolidado</li>
      <li><span class="ind-mini ind-mini-gap" aria-hidden="true"></span> anel tracejado: lacuna</li>
      <li><kbd>Ctrl</kbd><kbd>K</kbd> pesquisar tudo</li>
      <li><kbd>Ctrl</kbd><kbd>Z</kbd> desfazer</li>
    </ul>`;
  return box;
}

/* ---------------------------------------------- taxonomia de tipos (0073) --
   O catalogo e SERVER STATE como qualquer outro: vem do banco, nunca e
   inventado aqui. O seletor do editor mostra o que a organizacao pode usar
   (sistema + proprios, ativos); "+ Adicionar tipo" persiste de verdade pela
   rota e so entao o tipo entra na lista. */
let catalogoTipos: TipoDeNo[] = [];

async function carregarTiposDeNo(): Promise<void> {
  try {
    catalogoTipos = await api.tiposDeNo(orgSlug, mapSlug);
    /* os rotulos de TODOS os tipos conhecidos (inclusive arquivados) — a tela
       precisa saber ler um no que ainda usa um tipo fora de uso */
    definirCatalogoTipos(catalogoTipos.map((t) => ({ key: t.key, label: t.label })));
    store.patchUi({});
  } catch {
    /* sem catalogo a tela nao para: o seletor cai nos 13 de sistema e o
       rotulo de um tipo desconhecido vira a propria chave, legivel */
    catalogoTipos = [];
  }
}

/* ------------------------------------------- guia operacional (0075A) --
   O guia e SERVER STATE, como a taxonomia. Ate o gate 0075A ele vinha
   compilado no bundle e era escolhido por external_id, sem organizacao —
   o vazamento entre clientes que o FOM-ARQ-0075 encontrou. Agora quem
   decide o que entregar e a API, pela organizacao do vinculo verificado.

   Falha nao vira guia de ninguem: limpa. Uma organizacao sem cobertura
   documental simplesmente nao mostra a caixa, que e o comportamento
   correto e ja existente para todo no fora do fluxo coberto. */
async function carregarGuiaOperacional(): Promise<void> {
  try {
    definirGuiaOperacional(await api.guiaOperacional(orgSlug, mapSlug));
  } catch {
    definirGuiaOperacional(null);
  }
  store.patchUi({});
}

function opcoesDeTipo(): {
  tipos: { key: string; label: string; scope: 'system' | 'organization' }[];
  criarTipo: (nome: string) => Promise<{ ok: true; valor: string; rotulo: string } | { ok: false; motivo: string }>;
} {
  return {
    tipos: catalogoTipos
      .filter((t) => t.status === 'active')
      .map((t) => ({ key: t.key, label: t.label, scope: t.scope })),
    criarTipo: async (nome: string) => {
      if (papelAtual === 'viewer') {
        return { ok: false as const, motivo: 'Criar tipo e acao de Editor ou Owner.' };
      }
      try {
        const criado = await api.criarTipoDeNo(orgSlug, mapSlug, { label: nome });
        catalogoTipos = [...catalogoTipos.filter((t) => t.key !== criado.key), criado];
        definirCatalogoTipos(catalogoTipos.map((t) => ({ key: t.key, label: t.label })));
        store.mensagem('ok', `Tipo "${criado.label}" criado nesta organizacao.`);
        void atualizarContexto();
        return { ok: true as const, valor: criado.key, rotulo: criado.label };
      } catch (err) {
        const e = err as { status?: number; message?: string };
        if (e.status === 403) return { ok: false as const, motivo: 'Seu papel nesta organizacao nao permite criar tipos.' };
        return { ok: false as const, motivo: e.message ?? 'Nao foi possivel criar o tipo.' };
      }
    },
  };
}

/* ------------------------------------------- entrada organizacional ------ */
/* A PORTA DE ENTRADA e a ORGANIZACAO (Fase 1AB): ao abrir o FOM, a primeira
   cena e a estrutura — GNI como eixo, departamentos como pilares, Financeiro
   transversal. Nao e um mapa novo: e a MESMA cena organizacional (Brain do
   eixo de gestao), aberta de cara. Esc / "‹ mapa" levam a arvore completa.
   Em mapas sem eixo de gestao (ex.: org de teste), cai na raiz. */
let entradaOrganizacionalFeita = false;

async function entrarNaOrganizacao(): Promise<void> {
  /* GATE 0072 (artefato): a ORGANIZACAO e a arvore deterministica com zoom
     semantico dentro da stagebox — "como a empresa se compoe". A leitura
     radial de conhecimento continua a um clique (perspectiva Conhecimento). */
  perspectivaAtual = 'organizacao';
  sairDoCerebro();
  const nos = store.estado.server.nos;
  const eixo = nos.find((n) => n.external_id === 'gni')
    ?? nos.find((n) => !n.parent_external_id);
  if (!eixo) { pintarPerspectiva(); return; }
  definirWorkspace('organizacao');
  store.selecionar(eixo.external_id);
  pintarPerspectiva();
  /* o nivel de entrada e o maior que cabe legivel na caixa da cena */
  requestAnimationFrame(() => requestAnimationFrame(() => apresentarNoNivelQueCabe()));
  await Promise.resolve();
}

/* PROCESSOS (artefato): cena de trilhos com estacoes na ordem real; a raiz
   real e o no `procs` (DIMENSOES). Fora dela o palco volta ao mapa neutro. */
function abrirProcessos(): void {
  const d = DIMENSOES.find((x) => x.id === 'processos');
  sairDoCerebro();
  perspectivaAtual = 'processos';
  definirWorkspace('processos');
  if (d?.raiz) {
    for (const n of store.estado.server.nos) {
      if (n.parent_external_id === d.raiz) store.expandirCaminho(n.external_id);
    }
    store.expandirCaminho(d.raiz);
    store.selecionar(d.raiz);
  }
  pintarPerspectiva();
  store.patchUi({});
}

/* muda o WORKSPACE do palco: classe e atributo para o CSS, cabecalho e
   cartoes pintados por pintarWorkspace() a cada pintar */
function definirWorkspace(ws: Workspace): void {
  const mudou = wsAtual !== ws;
  wsAtual = ws;
  const palco = document.getElementById('palco');
  if (!palco) return;
  palco.classList.toggle('modo-workspace', ws !== null);
  if (ws) palco.setAttribute('data-ws', ws); else palco.removeAttribute('data-ws');
  if (ws === 'processos') processos?.mostrar(); else processos?.esconder();
  /* a stagebox so ganha a altura final depois do layout do CSS: reapresentar
     no frame seguinte faz a camera decidir com a caixa REAL, em vez de com a
     caixa antiga (arvore cortada ao entrar no workspace) */
  if (mudou && ws === 'organizacao') {
    requestAnimationFrame(() => requestAnimationFrame(() => apresentarNoNivelQueCabe()));
  }
}

function pintarWorkspace(): void {
  const head = document.getElementById('ws-head');
  const cards = document.getElementById('ws-cards');
  const titulo = document.getElementById('scene-title');
  const legenda = document.getElementById('scene-legend');
  if (!head || !cards || !titulo || !legenda) return;
  const e = store.estado;
  head.hidden = wsAtual === null;
  cards.hidden = wsAtual === null;
  titulo.hidden = wsAtual === null;
  legenda.hidden = wsAtual !== 'organizacao';
  pintarListaEstrutura();
  if (wsAtual === null) return;
  const ativos = e.server.nos.filter((n) => !n.removed_at);
  if (wsAtual === 'organizacao') {
    const nivel = nivelAtual ?? 'livre';
    const visiveis = mapa?.layout?.posicoes.length ?? 0;
    const eixo = ativos.find((n) => n.external_id === 'gni') ?? ativos.find((n) => !n.parent_external_id);
    head.innerHTML = `<div><p class="cc-eyebrow">Workspace · Organizacao</p><h1 class="cc-titulo">Como a empresa se compoe</h1>
      <p class="cc-sub">Arvore deterministica da BASE congelada. Afaste para ver <b>capsulas</b> com contagem e distribuicao epistemica; aproxime para ver grupos, nos e detalhe. O zoom muda a representacao, nao so a escala.</p></div>
      <div class="legend" id="org-legend"><span class="mono">${nivel}</span><span>◆ conhecimento</span><span>◇ lacuna</span><span>capsula = ramo fechado</span></div>`;
    titulo.innerHTML = `<span class="cc-eyebrow">Cena</span><b></b>`;
    titulo.querySelector('b')!.textContent = `${eixo?.name ?? 'Organizacao'} · ${nivel} · ${visiveis} visiveis`;
    legenda.innerHTML = `<span><i style="background:var(--ev-e2)"></i>lacuna aberta</span><span><i style="background:var(--ev-tobe)"></i>futuro (TO-BE)</span><span><i style="background:var(--ev-e3);border-radius:0;height:2px"></i>relacao</span><span><i style="border:1.5px dashed #8c7a4e;background:none"></i>USER</span>`;
    const lod: [string, string, string][] = [
      ['capsulas', 'afastar', 'Subarvore agregada: nome, descendentes, conhecimento, lacunas, TO-BE e barra epistemica. A visao do todo comunica onde a empresa sabe e onde nao sabe.'],
      ['grupos', 'padrao', 'Areas abertas ate dois niveis; funcoes e processos como capsulas. Anel ambar = lacuna; rosa = futuro; tracejado = USER.'],
      ['nos', 'aproximar', 'Tres niveis abertos; cartao completo com classe escrita, tipo e contagens. Clique seleciona; + abre o ramo (ancorado); Esc limpa.'],
    ];
    cards.innerHTML = lod.map(([n, z, d]) => `<div class="cardx"><div class="ch"><span class="h3">${n}</span><span class="mono xs muted">${z}</span>${nivel === n ? '<span class="cc-tag cc-tag-privado">ativo</span>' : ''}</div><p class="cc-nota">${d}</p></div>`).join('');
    return;
  }
  /* processos */
  const procs = ativos.find((n) => n.external_id === (DIMENSOES.find((x) => x.id === 'processos')?.raiz ?? ''));
  const h06 = ativos.find((n) => n.external_id === 'h06');
  const handoffEtapa = ativos.find((n) => n.external_id === 'proc-handoff');
  const nPassos = handoffEtapa ? ativos.filter((n) => n.parent_external_id === handoffEtapa.external_id && n.child_kind === 'flow').length : 0;
  head.innerHTML = `<div><p class="cc-eyebrow">Workspace · Processos</p><h1 class="cc-titulo">Como o trabalho acontece</h1>
    <p class="cc-sub">Estacoes na ordem do <span class="mono">ordinal</span> da BASE. Setas de fluxo representam ordem real. A transicao comercial → operacao existe em nivel de departamento; o elo etapa-a-etapa nao e comprovado e aparece como composicao visual.</p></div>
    <div class="legend"><span><i style="background:var(--ev-e1)"></i>E1</span><span><i style="background:var(--ev-e3)"></i>E3</span><span><i style="border:1.5px dashed var(--ev-nd);background:none"></i>ND</span><span><i style="background:var(--ev-e2)"></i>lacuna</span></div>`;
  titulo.innerHTML = `<span class="cc-eyebrow">Cena</span><b></b>`;
  titulo.querySelector('b')!.textContent = `${procs?.name ?? 'Processos'} · fluxo real`;
  cards.innerHTML = `
    <div class="cardx"><div class="ch"><span class="h3">Responsaveis</span><span class="cc-tag cc-tag-na">parcial</span></div><p class="cc-nota">O dominio nao tem "responsavel por no". O guia operacional cita papeis por etapa em texto com fonte. Estrutura futura: relacao tipada no → funcao.</p><p class="cc-nota xs">DEPENDE DE FUTURO GATE DE ARQUITETURA DE CONHECIMENTO</p></div>
    <div class="cardx"><div class="ch"><span class="h3">Decisoes e excecoes</span><span class="cc-tag cc-tag-na">texto livre</span></div><p class="cc-nota">Vivem no contrato de no (16 listas + 6 campos de automacao, camada PRIVATE) e no guia. Legiveis por humano; nao verificaveis por maquina.</p><p class="cc-nota xs">DEPENDE DE FUTURO GATE DE ARQUITETURA DE CONHECIMENTO</p></div>
    <div class="cardx"><div class="ch"><span class="h3">Handoff</span><span class="cc-tag cc-tag-mapa">dado real</span></div><p class="cc-nota">${h06 ? `Dois objetos distintos: <b>h06</b> (${h06.name}, objeto de auditoria sob ${ativos.find((n) => n.external_id === h06.parent_external_id)?.name ?? 'o pai real'})` : 'Objeto de auditoria h06: nao carregado neste mapa'}${handoffEtapa ? ` e <b>proc-handoff</b>, etapa do fluxo com ${nPassos} passos. Nao sao duplicata.` : '.'}</p>${h06 ? '<button type="button" class="botao" id="ws-abrir-h06">Abrir anatomia h06</button>' : ''}</div>`;
  cards.querySelector('#ws-abrir-h06')?.addEventListener('click', () => irParaNo('h06'));
}

/* REPRESENTACAO SECUNDARIA (artefato, telas estreitas): onde o mapa espacial
   nao cabe, a estrutura vira LISTA NAVEGAVEL — filhos do contexto atual, com
   a classe escrita, o total da subarvore e as lacunas. O CSS decide quando
   ela aparece; aqui so derivamos do mesmo dado. Nada e inventado. */
function pintarListaEstrutura(): void {
  const host = document.getElementById('ws-lista');
  if (!host) return;
  if (wsAtual !== 'organizacao') { host.hidden = true; return; }
  host.hidden = false;
  host.innerHTML = '';
  const e = store.estado;
  const ativos = e.server.nos.filter((n) => !n.removed_at);
  const sel = e.ui.selecionado ? noPorId(e, e.ui.selecionado) : null;
  const contexto = sel ?? ativos.find((n) => n.external_id === 'gni') ?? ativos.find((n) => !n.parent_external_id) ?? null;
  if (!contexto) { host.hidden = true; return; }
  const agregados = agregarSubarvores(ativos);
  const filhos = ativos.filter((n) => n.parent_external_id === contexto.external_id).sort(compararIrmaos);

  const ch = document.createElement('div');
  ch.className = 'ch';
  const h = document.createElement('span');
  h.className = 'h3';
  h.textContent = contexto.name;
  ch.appendChild(h);
  const tag = document.createElement('span');
  tag.className = 'cc-tag cc-tag-privado';
  tag.textContent = 'lista';
  ch.appendChild(tag);
  host.appendChild(ch);

  if (contexto.parent_external_id) {
    const subir = document.createElement('button');
    subir.type = 'button';
    subir.className = 'botao botao-fantasma ws-lista-subir';
    subir.textContent = '‹ subir';
    subir.addEventListener('click', () => irParaNo(contexto.parent_external_id!));
    host.appendChild(subir);
  }
  const ul = document.createElement('ul');
  ul.className = 'ws-lista-itens';
  for (const f of filhos) {
    const li = document.createElement('li');
    const chip = document.createElement('span');
    chip.className = 'ev-chip ev-' + f.st;
    chip.style.setProperty('--cor', COR_ST[f.st]);
    chip.textContent = f.st;
    li.appendChild(chip);
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'evidencia-link ws-lista-no';
    b.textContent = f.name;
    b.addEventListener('click', () => irParaNo(f.external_id));
    li.appendChild(b);
    const meta = document.createElement('span');
    meta.className = 'ws-lista-meta mono xs muted';
    const a = agregados.get(f.external_id);
    meta.textContent = `${a?.total ?? 0} · ◇ ${f.gaps.length + (a?.gaps ?? 0)}`;
    li.appendChild(meta);
    ul.appendChild(li);
  }
  if (!filhos.length) {
    const vazio = document.createElement('p');
    vazio.className = 'cc-nota';
    vazio.textContent = 'Este contexto nao tem filhos: e uma folha da estrutura.';
    host.appendChild(vazio);
  }
  host.appendChild(ul);
}

/* aviso EXPLICITO da perspectiva: nada some em silencio */
function pintarPerspectiva(): void {
  /* CORRECAO 0081 · UMA perspectiva acesa por vez. Conhecimento e Evidencias
     vivem no MESMO grupo das dimensoes (FOM-ARQ-0043) e agora tomam o palco:
     manter Organizacao tambem acesa fazia o grupo mostrar duas escolhas ativas
     e sugerir que o Conhecimento era uma segunda CAMADA sobre a Organizacao.
     O contexto nao se perde — ele vive no breadcrumb CONTEXTO, nao no botao. */
  const noCerebro = modoVisao === 'cerebro';
  for (const b of Array.from(document.querySelectorAll('[data-dimensao]'))) {
    b.classList.toggle('dimensao-ativa',
      !noCerebro && (b as HTMLElement).dataset.dimensao === perspectivaAtual);
  }
  const aviso = document.getElementById('aviso-perspectiva');
  if (!aviso) return;
  /* o aviso fala do que a ARVORE deixa de fora: sem arvore em cena, ele mente */
  if (perspectivaAtual === 'organizacao' && !noCerebro) {
    const { excluidos } = filtrarPerspectivaOrganizacao(store.estado.server.nos);
    aviso.hidden = false;
    aviso.textContent = `perspectiva Organizacao — ${excluidos} objetos de processo fora desta leitura (veja em Processos)`;
  } else {
    aviso.hidden = true;
  }
}

/* --------------------------------------------------------- dimensoes ----- */
/* Organizacao × Processos × Produtos: PERSPECTIVAS de navegacao sobre os
   MESMOS dados — atalhos de camera/contexto, nunca mapas paralelos. */
function montarDimensoes(): void {
  const host = document.getElementById('dimensoes');
  if (!host) return;
  host.innerHTML = '';
  for (const d of DIMENSOES) {
    const b = document.createElement('button');
    b.className = 'dimensao-botao';
    b.dataset.dimensao = d.id;
    b.textContent = d.rotulo;
    b.title = d.dica;
    b.addEventListener('click', () => {
      if (d.id === 'organizacao') {
        /* direto na CENA organizacional — sem exigir descobrir o modo
           Conhecimento (achado da homologacao executiva) */
        perspectivaAtual = 'organizacao';
        void entrarNaOrganizacao();
      } else if (d.raiz) {
        /* PROCESSOS abre a cena de trilhos do artefato (gate 0072) */
        fecharSuperficies();
        abrirProcessos();
      } else {
        /* NEXT-GEN: Produtos e uma SUPERFICIE propria (capsulas + cadeia
           derivada), nao um popover sobre o palco vazio */
        void b;
        abrirProdutos();
        return;
      }
      pintarPerspectiva();
      store.patchUi({});
    });
    host.appendChild(b);
  }

  /* PERSPECTIVAS DE VISUALIZACAO do contexto atual (1AD.1): Conhecimento e
     Evidencias moram no MESMO grupo — o usuario nao precisa aprender a
     diferenca entre "dimensao" e "modo". Contexto nunca se perde. */
  const bCerebro = document.createElement('button');
  bCerebro.className = 'dimensao-botao';
  bCerebro.dataset.modo = 'cerebro';
  bCerebro.textContent = 'Conhecimento';
  bCerebro.title = 'o que sabemos sobre o contexto selecionado';
  bCerebro.addEventListener('click', () => {
    if (modoVisao === 'cerebro' && !(cerebro?.evidenciasVisiveis ?? false)) sairDoCerebro();
    else void entrarNoCerebro().then(() => { cerebro?.fecharEvidencias(); pintarModos(); });
  });
  host.appendChild(bCerebro);

  const bEvid = document.createElement('button');
  bEvid.className = 'dimensao-botao';
  bEvid.dataset.modo = 'evidencias';
  bEvid.textContent = 'Evidencias';
  bEvid.title = 'a camada probatoria do contexto selecionado (fonte, E1/E2/E3, lacunas, investigacoes)';
  bEvid.addEventListener('click', () => {
    void (async () => {
      if (modoVisao !== 'cerebro') await entrarNoCerebro();
      cerebro?.alternarEvidencias();
      pintarModos();
    })();
  });
  host.appendChild(bEvid);
  pintarModos();
}

/* ESTADO (lentes): as definicoes acessiveis SEM hover — popover persistente */
function montarInfoLentes(): void {
  const botao = document.getElementById('lentes-info');
  const pop = document.getElementById('lentes-popover');
  if (!botao || !pop) return;
  pop.innerHTML = '';
  for (const l of LENTES) {
    const linha = document.createElement('p');
    linha.className = 'lente-def';
    const nome = document.createElement('b');
    nome.textContent = l.rotulo;
    linha.appendChild(nome);
    linha.appendChild(document.createTextNode(' — ' + l.dica));
    pop.appendChild(linha);
  }
  botao.addEventListener('click', (ev) => {
    ev.stopPropagation();
    pop.hidden = !pop.hidden;
    botao.setAttribute('aria-expanded', String(!pop.hidden));
  });
  document.addEventListener('click', (ev) => {
    if (!pop.hidden && !pop.contains(ev.target as Node)) pop.hidden = true;
  });
}

/* os produtos sao NOS REAIS — o popover so lista o que existe no dado */
function alternarPopoverProdutos(ancora: HTMLElement): void {
  const existente = document.getElementById('popover-produtos');
  if (existente) { existente.remove(); return; }
  const pop = document.createElement('div');
  pop.id = 'popover-produtos';
  pop.className = 'popover-produtos';
  pop.setAttribute('role', 'menu');
  const reais = (PRODUTOS as readonly string[])
    .map((id) => store.estado.server.nos.find((n) => n.external_id === id))
    .filter((n): n is NonNullable<typeof n> => !!n);
  if (!reais.length) {
    const vazio = document.createElement('p');
    vazio.textContent = 'nenhum produto/fluxo mapeado';
    pop.appendChild(vazio);
  }
  for (const n of reais) {
    const item = document.createElement('button');
    item.className = 'popover-produto-item';
    item.textContent = n.name;
    item.addEventListener('click', () => {
      pop.remove();
      irParaNo(n.external_id);
      void entrarNoCerebro();
    });
    pop.appendChild(item);
  }
  const r = ancora.getBoundingClientRect();
  pop.style.left = r.left + 'px';
  pop.style.top = r.bottom + 6 + 'px';
  document.body.appendChild(pop);
  const fechar = (ev: MouseEvent): void => {
    if (!pop.contains(ev.target as Node) && ev.target !== ancora) {
      pop.remove();
      document.removeEventListener('click', fechar, true);
    }
  };
  setTimeout(() => document.addEventListener('click', fechar, true), 0);
}

/* ------------------------------------------------------- modos de visao -- */
/* MAPA = estrutura (esqueleto). CONHECIMENTO = a rede neural do contexto
   selecionado. Mudar de modo NAO muda dado, selecao nem zoom do mapa. */
function pintarModos(): void {
  /* os botoes de visualizacao vivem no grupo PERSPECTIVA (1AD.1) */
  for (const b of Array.from(document.querySelectorAll('[data-modo]'))) {
    const modo = (b as HTMLButtonElement).dataset.modo;
    const ativo = modo === 'cerebro'
      ? modoVisao === 'cerebro' && !(cerebro?.evidenciasVisiveis ?? false)
      : modo === 'evidencias'
        ? modoVisao === 'cerebro' && (cerebro?.evidenciasVisiveis ?? false)
        : modoVisao === (modo as 'mapa' | 'cerebro');
    b.classList.toggle('modo-ativo', ativo);
    b.setAttribute('aria-pressed', String(ativo));
  }
  pintarCamadasInertes();
}

/* CORRECAO 0081 · PERSPECTIVA nao e CAMADA. O grupo CAMADAS (relacoes,
   conhecimento, lacunas) so molda a ARVORE estrutural: nao alcanca a cena
   radial do cerebro. Enquanto a perspectiva Conhecimento/Evidencias esta em
   cena os tres continuam VISIVEIS e passam a INERTES, com o motivo escrito no
   title — o mesmo criterio de governanca do FOM-ARQ-0043 (acao visivel,
   desabilitada e explicada). Nenhum estado do usuario e apagado: ao voltar
   para a arvore o realce que estava ligado continua ligado. */
function pintarCamadasInertes(): void {
  const noCerebro = modoVisao === 'cerebro';
  const motivo = 'Camada da arvore estrutural — volte a Organizacao ou Processos para usar.';
  const alvos: [string, string][] = [
    ['ver-relacoes', 'Mostra as relacoes no mapa estrutural'],
    ['nav-conhecimento', 'Realca no mapa o que ja esta consolidado'],
    ['nav-lacunas', 'Realca no mapa o que ainda falta descobrir'],
  ];
  for (const [id, dica] of alvos) {
    const el = document.getElementById(id) as HTMLButtonElement | HTMLInputElement | null;
    if (!el) continue;
    el.disabled = noCerebro;
    const hospedeiro = el.closest('.camada-toggle') ?? el;
    (hospedeiro as HTMLElement).title = noCerebro ? motivo : dica;
  }
}

function montarModos(): void {
  /* mantido por compatibilidade: a montagem agora acontece em
     montarDimensoes (grupo unico de PERSPECTIVA) */
  pintarModos();
}

async function entrarNoCerebro(): Promise<void> {
  /* CORRECAO 0081 · UMA CENA POR VEZ: Conhecimento e Evidencias sao
     PERSPECTIVAS (FOM-ARQ-0043) e, como as outras, tomam o palco. Sem isto,
     entrar no cerebro vindo de Produtos/Command Center deixava a superficie
     anterior viva atras dele, com o palco ainda em modo-comando. */
  fecharSuperficies();
  /* precisa de um contexto: o selecionado, ou a raiz do mapa */
  let sel = store.estado.ui.selecionado;
  if (!sel) {
    sel = store.estado.server.nos.find((n) => !n.parent_external_id)?.external_id ?? null;
    if (!sel) return;
    store.selecionar(sel);
  }
  modoVisao = 'cerebro';
  document.getElementById('palco')?.classList.add('modo-cerebro');
  /* o mapa/cerebro e protagonista: o painel encolhe para instrumento */
  document.body.classList.add('em-cerebro');
  /* 1AE.3: a cena pinta IMEDIATAMENTE com o que ja se sabe — nunca uma
     janela em que a arvore/vazio pareca ser a cena da perspectiva.
     Investigacoes (dado real) chegam em seguida e repintam; falha vira
     lista vazia — nunca numero inventado. */
  invsCerebro = invsCerebro ?? [];
  pintarCerebro();
  cerebro?.mostrar();
  pintarModos();
  pintarPerspectiva();
  invsCerebro = await api.investigacoes(orgSlug, mapSlug).catch(() => invsCerebro ?? []);
  pintarCerebro();
}

/* --------------------------------------------------- superficies next-gen -- */
function fecharSuperficies(): void {
  const palco = document.getElementById('palco');
  if (comando?.aberta) comando.esconder();
  if (produtos?.aberta) produtos.esconder();
  if (conhecimento?.aberta) conhecimento.esconder();
  palco?.classList.remove('modo-comando');
  pintarNav();
}

function abrirConhecimento(foco: 'sabemos' | 'lacunas'): void {
  if (!conhecimento) return;
  /* CORRECAO 0081 · uma cena por vez (ver entrarNoCerebro) */
  sairDoCerebro();
  comando?.esconder();
  produtos?.esconder();
  gaveta?.fechar();
  document.getElementById('palco')?.classList.add('modo-comando');
  conhecimento.render(store.estado.server.nos, store.estado.server.relacoes, foco);
  conhecimento.mostrar();
  pintarNav();
}

/* o proximo nivel MENOR a partir da expansao atual (para o zoom semantico) */
function proximoNivelMenor(): NivelSemantico | null {
  const e = store.estado;
  let maxProf = -1;
  for (const n of e.server.nos) {
    if (n.removed_at || !e.ui.expandidos.has(n.external_id)) continue;
    if ((n.depth ?? 0) > maxProf) maxProf = n.depth ?? 0;
  }
  /* um no expandido de profundidade d mostra filhos em d+1 */
  if (maxProf >= 2) return 'grupos' === nivelAtual || maxProf === 2 ? 'capsulas' : 'grupos';
  if (maxProf === 1 && nivelAtual !== 'capsulas') return 'capsulas';
  return null;
}

function dadosCommandCenter(): DadosCommandCenter {
  const e = store.estado;
  return {
    nos: e.server.nos,
    relacoes: e.server.relacoes,
    investigacoes: dadosCc.invs,
    brain: dadosCc.brain,
    publicacoes: dadosCc.pubs,
    historico: dadosCc.hist.length ? dadosCc.hist : e.server.historico,
    papel: papelAtual,
  };
}

function abrirComando(): void {
  if (!comando) return;
  /* CORRECAO 0081 · uma cena por vez (ver entrarNoCerebro) */
  sairDoCerebro();
  produtos?.esconder();
  gaveta?.fechar();
  document.getElementById('palco')?.classList.add('modo-comando');
  comando.render(dadosCommandCenter());
  comando.mostrar();
  pintarNav();
}

function abrirProdutos(): void {
  if (!produtos) return;
  /* CORRECAO 0081 · uma cena por vez (ver entrarNoCerebro) */
  sairDoCerebro();
  comando?.esconder();
  gaveta?.fechar();
  document.getElementById('palco')?.classList.add('modo-comando');
  produtos.render(store.estado.server.nos, store.estado.server.relacoes);
  produtos.mostrar();
  pintarNav();
}

/* repinta as superficies abertas quando o estado muda (nunca inventa dado) */
function pintarSuperficies(): void {
  if (comando?.aberta) comando.render(dadosCommandCenter());
  if (produtos?.aberta) produtos.render(store.estado.server.nos, store.estado.server.relacoes);
  if (conhecimento?.aberta) conhecimento.render(store.estado.server.nos, store.estado.server.relacoes);
  const lod = document.getElementById('toolbar-lod');
  if (lod) lod.textContent = nivelAtual ? ({ capsulas: 'capsulas', grupos: 'grupos', nos: 'nos' } as const)[nivelAtual] : 'livre';
  const atencao = document.getElementById('conta-atencao');
  if (atencao && comando) {
    /* contagem de filas com atencao: derivada da mesma funcao pura */
    const filas = derivarFilasParaContagem();
    atencao.textContent = filas ? String(filas) : '';
  }
}

function derivarFilasParaContagem(): number {
  const d = dadosCommandCenter();
  const nos = d.nos.filter((n) => !n.removed_at);
  let n = 0;
  if (nos.some((x) => x.gaps.length)) n++;
  if (nos.some((x) => (x.st === 'E1' || x.st === 'E2') && !(x.src ?? '').trim())) n++;
  if (d.investigacoes && d.investigacoes.some((i) => i.status === 'aberta')) n++;
  if (nos.some((x) => ehFuturo(x))) n++;
  if (d.brain?.granted && (d.brain.pending ?? 0) > 0) n++;
  const ultima = d.publicacoes?.[0];
  if (!ultima || (d.historico[0] && d.historico[0].at > ultima.published_at)) n++;
  return n;
}

function pintarNav(): void {
  const ativo = comando?.aberta ? 'nav-comando'
    : produtos?.aberta ? 'nav-produtos'
      : conhecimento?.aberta ? 'nav-conhecimento-ws'
        : wsAtual === 'organizacao' || perspectivaAtual === 'organizacao' ? 'nav-organizacao'
          : wsAtual === 'processos' || perspectivaAtual === 'processos' ? 'nav-processos'
            : 'nav-mapa';
  for (const id of ['nav-comando', 'nav-mapa', 'nav-organizacao', 'nav-processos', 'nav-produtos', 'nav-conhecimento-ws']) {
    const b = document.getElementById(id);
    if (!b) continue;
    b.classList.toggle('sidebar-item-ativo', id === ativo);
    b.setAttribute('aria-current', id === ativo ? 'page' : 'false');
  }
}

/* ZOOM SEMANTICO v1 — NIVEL por representacao, nao por escala.
   O nivel escolhe QUE profundidade fica aberta; o motor de layout (que ja e
   deterministico) recompoe a arvore; cada ramo fechado vira uma CAPSULA
   agregada (descendentes, conhecimento, lacunas, distribuicao epistemica —
   dado real da subarvore). Zoom/pan continuam livres. */
type NivelSemantico = 'capsulas' | 'grupos' | 'nos';
let nivelAtual: NivelSemantico | null = null;
function definirNivel(nivel: NivelSemantico, comCamera = true): void {
  fecharSuperficies();
  sairDoCerebro();
  /* o nivel e um instrumento da arvore: na cena de processos, volta a ela */
  if (wsAtual === 'processos') { definirWorkspace(null); perspectivaAtual = null; pintarPerspectiva(); }
  const profundidade = nivel === 'capsulas' ? 1 : nivel === 'grupos' ? 2 : 3;
  const expandidos = new Set<string>();
  for (const n of store.estado.server.nos) {
    if (n.removed_at) continue;
    if ((n.depth ?? 99) <= profundidade) expandidos.add(n.external_id);
  }
  nivelAtual = nivel;
  store.patchUi({ expandidos });
  /* enquadra se couber LEGIVEL; senao fixa o piso de legibilidade e centraliza
     a raiz — zoom semantico nunca produz texto microscopico */
  if (comCamera) requestAnimationFrame(() => mapa?.apresentar());
}

/* ENTRADA DO WORKSPACE (gate 0072, artefato): "o zoom muda a representacao,
   nao so a escala" — ao abrir a Organizacao o produto escolhe o MAIOR nivel
   que cabe LEGIVEL na caixa da cena (nos → grupos → capsulas) e enquadra.
   Assim a estrutura aparece inteira, sem texto microscopico e sem corte. */
function apresentarNoNivelQueCabe(): void {
  const piso = mapa?.zoomLegivel ?? 0.75;
  const ordem: NivelSemantico[] = ['nos', 'grupos', 'capsulas'];
  for (const nivel of ordem) {
    definirNivel(nivel, false);
    const z = mapa?.zoomDeAjuste() ?? 0;
    if (z >= piso || nivel === 'capsulas') break;
  }
  mapa?.ajustar();
}
function pintarNivel(): void {
  const host = document.getElementById('nivel-seg');
  if (!host) return;
  /* o nivel so fica aceso enquanto a expansao corresponde a ele */
  const e = store.estado;
  const prof = nivelAtual === 'capsulas' ? 1 : nivelAtual === 'grupos' ? 2 : nivelAtual === 'nos' ? 3 : -1;
  let coincide = prof >= 0;
  if (coincide) {
    for (const n of e.server.nos) {
      if (n.removed_at) continue;
      const deveria = (n.depth ?? 99) <= prof;
      if (deveria !== e.ui.expandidos.has(n.external_id)) { coincide = false; break; }
    }
  }
  for (const b of Array.from(host.querySelectorAll<HTMLButtonElement>('[data-nivel]'))) {
    b.setAttribute('aria-pressed', String(coincide && b.dataset.nivel === nivelAtual));
  }
}

function sairDoCerebro(): void {
  if (modoVisao !== 'cerebro') return;
  modoVisao = 'mapa';
  document.getElementById('palco')?.classList.remove('modo-cerebro');
  document.body.classList.remove('em-cerebro');
  cerebro?.esconder();
  pintarModos();
  pintarPerspectiva();
  /* gate 0072: a perspectiva PROCESSOS vive na cena de trilhos (workspace);
     sair do cerebro nao a derruba — so nav-mapa / nivel / alvo externo */
}

function pintarCerebro(): void {
  if (modoVisao !== 'cerebro') return;
  const sel = store.estado.ui.selecionado;
  const no = sel ? noPorId(store.estado, sel) : null;
  if (!no) { sairDoCerebro(); return; }
  cerebro?.render(no, store.estado.server.nos, store.estado.server.relacoes, invsCerebro ?? []);
}

/* ---------------------------------------------------------------- lentes -- */
function montarLentes(): void {
  const host = document.getElementById('lentes');
  if (!host) return;
  host.innerHTML = '';
  for (const l of LENTES) {
    const b = document.createElement('button');
    b.className = 'lente-botao' + (l.id === lenteAtual ? ' lente-ativa' : '');
    b.dataset.lente = l.id;
    b.title = l.dica;
    b.setAttribute('aria-pressed', String(l.id === lenteAtual));
    b.innerHTML = '<span></span><b class="lente-conta"></b>';
    b.querySelector('span')!.textContent = l.rotulo;
    b.addEventListener('click', () => {
      lenteAtual = l.id;
      mapa?.setLente(l.id);
      store.patchUi({});   /* repinta: o filtro de layout da lente vive no pintar */
      /* A estrutura futura costuma morar em ramos recolhidos: uma lente que
         mostra o futuro sem abrir o caminho ate ele mostraria o vazio. Abrir
         o caminho e derivar a vista do dado — zoom e pan nao se mexem. */
      if (l.id === 'futura' || l.id === 'mudancas') {
        for (const n of store.estado.server.nos) {
          if (ehFuturo(n)) store.expandirCaminho(n.external_id);
        }
      }
      if (l.id === 'futura' || l.id === 'mudancas') {
        requestAnimationFrame(() => mapa?.enquadrarFuturoSePreciso());
      }
      for (const q of host.querySelectorAll('.lente-botao')) {
        q.classList.toggle('lente-ativa', (q as HTMLElement).dataset.lente === l.id);
        q.setAttribute('aria-pressed', String((q as HTMLElement).dataset.lente === l.id));
      }
    });
    host.appendChild(b);
  }
  pintarLentes();
}

function pintarLentes(): void {
  const host = document.getElementById('lentes');
  if (!host) return;
  const c = contarPorLente(store.estado.server.nos);
  const poe = (id: Lente, n: number): void => {
    const b = host.querySelector(`[data-lente="${id}"] .lente-conta`);
    if (b) b.textContent = String(n);
  };
  poe('atual', c.atual);
  poe('futura', c.futura);
  poe('completa', c.completa);
  poe('mudancas', c.mudancas);
}

/* -------------------------------------------------------- desfazer/refazer - */
async function executarDesfazer(): Promise<void> {
  if (!histLocal.podeDesfazer) {
    store.mensagem('aviso', 'Nada para desfazer nesta sessao.');
    return;
  }
  try {
    const rotulo = await histLocal.desfazer();
    if (rotulo) store.mensagem('ok', 'Desfeito: ' + rotulo + '. A trilha guarda as duas operacoes.');
  } catch (err) {
    if (err instanceof ConflitoError) {
      store.mensagem('erro',
        'Nao da para desfazer: o estado mudou no servidor desde a sua acao. '
        + 'Recarregue para ver o estado atual.');
    } else {
      tratarErro(err, 'desfazer');
    }
  }
}

async function executarRefazer(): Promise<void> {
  if (!histLocal.podeRefazer) {
    store.mensagem('aviso', 'Nada para refazer.');
    return;
  }
  try {
    const rotulo = await histLocal.refazer();
    if (rotulo) store.mensagem('ok', 'Refeito: ' + rotulo + '.');
  } catch (err) {
    if (err instanceof ConflitoError) {
      store.mensagem('erro', 'Nao da para refazer: o estado mudou no servidor.');
    } else {
      tratarErro(err, 'refazer');
    }
  }
}

/* O ciclo do produto em 8 passos — a jornada da referencia visual, como guia
   sob demanda e nao como chrome permanente. Texto, nao promessa: cada passo
   descreve o que o produto JA faz. */
function abrirGuia(): void {
  const d = document.getElementById('dialogo');
  if (!d) return;
  d.innerHTML = '';
  d.hidden = false;
  const box = document.createElement('div');
  box.className = 'dialogo guia';
  box.innerHTML = `
    <h3>Como o FOM funciona</h3>
    <p class="dialogo-sub">O mapa e o cerebro da operacao. O ciclo inteiro acontece nele:</p>
    <ol class="guia-passos">
      <li><b>Explorar</b> — navegue pelo mapa vivo. Zoom afasta o ruido; selecionar acende a vizinhanca.</li>
      <li><b>Investigar lacuna</b> — o anel tracejado e o que ainda nao se sabe. Enter na busca ou "investigar" abre a pergunta.</li>
      <li><b>Evidencias</b> — registre o que sustenta a resposta. Tela completa permite E1; sem evidencia, o teto e E3.</li>
      <li><b>Resposta e classificacao</b> — responda e feche dentro do teto que a evidencia permite.</li>
      <li><b>Conhecimento gerado</b> — o encerramento com efeito de conhecimento entra no mapa, com origem marcada.</li>
      <li><b>Publicar</b> — uma fotografia imutavel do conhecimento. A camada privada nunca atravessa.</li>
      <li><b>Trilha</b> — tudo o que aconteceu, em ordem, com quem fez. So cresce.</li>
      <li><b>Brain</b> — o agente observa e PROPOE. Nada vira conhecimento sem uma pessoa decidir.</li>
    </ol>
    <div class="dialogo-acoes"><button type="button" class="botao-principal" data-fechar>Entendi</button></div>`;
  box.querySelector('[data-fechar]')!.addEventListener('click', () => {
    d.hidden = true;
    d.innerHTML = '';
  });
  d.appendChild(box);
}

/* ------------------------------------------------------------------ camadas -- */
function selecionadoAtual(): FomNode | null {
  const e = store.estado;
  return e.ui.selecionado ? noPorId(e, e.ui.selecionado) ?? null : null;
}

async function abrirCamada(aba: Aba, no: FomNode | null): Promise<void> {
  if (!gaveta) return;
  await gaveta.abrir(aba, no);
}

async function abrirLacuna(no: FomNode, lacuna: string): Promise<void> {
  if (!gaveta) return;
  await gaveta.investigarLacuna(no, lacuna);
}

function pintarUsuario(papel: string): void {
  const chip = document.getElementById('usuario-chip');
  if (!chip) return;
  const nome = usuarioNome || '—';
  document.getElementById('usuario-nome')!.textContent = nome;
  document.getElementById('usuario-papel')!.textContent =
    papel === 'owner' ? 'Owner' : papel === 'editor' ? 'Editor' : 'Viewer';
  const avatar = chip.querySelector('.usuario-avatar')!;
  avatar.textContent = nome.trim().charAt(0).toUpperCase() || '?';
  chip.hidden = false;
}

/* A faixa de KPIs da referencia: PULSE (atividade por dia, da trilha),
   conhecimento, lacunas, investigacoes, propostas do Brain e ultima
   publicacao. Tudo vem do banco; "hoje" vem da trilha de hoje. */
function pintarKpis(): void {
  const host = document.getElementById('kpis');
  const c = store.estado.server.contagens;
  if (!host || !c) return;
  const vis = contagensVisao();
  host.hidden = false;
  const celula = (rotulo: string, valor: string, extra: string, classe = ''): string => `
    <div class="kpi ${classe}">
      <span class="kpi-rotulo">${rotulo}</span>
      <span class="kpi-valor">${valor}</span>
      <span class="kpi-extra">${extra}</span>
    </div>`;

  /* sparkline: contagem de mutacoes por dia (7 dias), da trilha real */
  const max = Math.max(1, ...kpi.porDia);
  const barras = kpi.porDia.map((v, i) =>
    `<rect x="${i * 9}" y="${18 - (v / max) * 16}" width="6" height="${Math.max(1, (v / max) * 16)}" rx="1"></rect>`).join('');
  const alerta = store.estado.server.fingerprint?.base_only_intact === false;

  const total7 = kpi.porDia.reduce((s, v) => s + v, 0);
  const dataPub = kpi.ultimaPub
    ? `${kpi.ultimaPub.at.slice(8, 10)}/${kpi.ultimaPub.at.slice(5, 7)} · ${kpi.ultimaPub.at.slice(11, 16)}`
    : '';
  host.innerHTML = `
    <button class="kpi kpi-pulse" id="kpi-pulse" title="O que aconteceu — abre a trilha">
      <span class="kpi-rotulo">${alerta ? 'BASE ALTERADA' : 'Atividade'}</span>
      <span class="kpi-valor">${alerta ? '!' : total7} <small>${alerta ? '' : 'alteracoes'}</small></span>
      <span class="kpi-extra">${alerta ? 'verificar fingerprint' : 'ultimos 7 dias'}</span>
    </button>
    ${celula('Conhecimento', String(vis.conhecimentos) + ' <small>consolidados</small>',
      kpi.hoje.conhecimento ? '+' + kpi.hoje.conhecimento + ' hoje' : 'sustentado por evidencia', 'kpi-know')}
    ${celula('Lacunas', String(vis.lacunas) + ' <small>abertas</small>', 'por descobrir', 'kpi-gap')}
    ${celula('Investigacoes', kpi.invsAbertas === null ? '—' : String(kpi.invsAbertas),
      kpi.hoje.investigacoes ? '+' + kpi.hoje.investigacoes + ' hoje' : 'em andamento', 'kpi-inv')}
    ${celula('Brain', !kpi.brainAtivo ? 'Inativo' : String(kpi.brainPendentes ?? 0),
      !kpi.brainAtivo ? 'analise operacional' : 'propostas aguardam voce', 'kpi-brain')}
    ${celula('Ultima publicacao', kpi.ultimaPub ? 'v' + kpi.ultimaPub.version : '—',
      kpi.ultimaPub ? dataPub : 'nenhuma ainda', 'kpi-pub')}
  `;
  /* atividade -> trilha: o clique leva a resposta de "o que houve?" */
  host.querySelector('#kpi-pulse')!.addEventListener('click', () => void abrirCamada('trilha', null));
  host.querySelector('.kpi-pub')?.addEventListener('click', () => void abrirCamada('publicacao', null));
  host.querySelector('.kpi-brain')?.addEventListener('click', () => void abrirCamada('brain', selecionadoAtual()));
  pintarSidebar();
  pintarVisaoGeral();
  /* as superficies leem as mesmas respostas (investigacoes, agente, publicacoes) */
  pintarSuperficies();
}

/* Busca os numeros que o mapa nao carrega: investigacoes, Brain e publicacoes.
   Cada falha vira "—", nunca numero inventado. */
async function carregarKpis(): Promise<void> {
  const [invs, brain, pubs, hist] = await Promise.all([
    api.investigacoes(orgSlug, mapSlug).catch(() => null),
    api.brainStatus(orgSlug, mapSlug).catch(() => null),
    api.publicacoes(orgSlug, mapSlug).catch(() => null),
    api.historico(orgSlug, mapSlug, 100).catch(() => [] as const),
  ]);
  kpi.invsAbertas = invs ? invs.filter((i) => i.status !== 'encerrada').length : null;
  /* o Command Center le as MESMAS respostas — nenhuma requisicao extra */
  dadosCc = { invs, brain, pubs, hist: hist ? [...hist] : [] };
  kpi.brainAtivo = brain?.granted === true;
  kpi.brainPendentes = brain?.pending ?? null;
  kpi.ultimaPub = pubs && pubs.length
    ? { version: pubs[0]!.version, at: pubs[0]!.published_at } : null;

  const hojeStr = new Date().toISOString().slice(0, 10);
  const doDia = (hist ?? []).filter((h) => h.at.slice(0, 10) === hojeStr);
  kpi.hoje = {
    conhecimento: doDia.filter((h) => /encerrar .*→/.test(h.action)).length,
    investigacoes: doDia.filter((h) => h.action.startsWith('inv: pergunta')).length,
  };
  /* mutacoes por dia, 7 dias — so o que a trilha registrou */
  const porDia: number[] = [];
  for (let d = 6; d >= 0; d--) {
    const dia = new Date(Date.now() - d * 86400000).toISOString().slice(0, 10);
    porDia.push((hist ?? []).filter((h) => h.at.slice(0, 10) === dia).length);
  }
  kpi.porDia = porDia;
  pintarKpis();
}

/* ------------------------------------------------------------------ pintar -- */
let avisoTimer: ReturnType<typeof setTimeout> | null = null;
let ultimoSelecionado: string | null = null;

function pintar(): void {
  const e = store.estado;

  const org = document.getElementById('org');
  if (org && e.server.organizacao) {
    org.textContent = `${e.server.organizacao.name} · ${e.server.mapa?.name ?? ''}`;
  }

  pintarSidebar();
  pintarVisaoGeral();
  pintarLentes();
  pintarKpis();
  pintarCerebro();
  pintarSuperficies();
  pintarNivel();
  pintarNav();
  /* cena de processos: derivada do dado a cada pintura (selecao inclusive) */
  if (wsAtual === 'processos') {
    processos?.render(e.server.nos, e.server.relacoes, e.ui.selecionado,
      DIMENSOES.find((x) => x.id === 'processos')?.raiz ?? 'procs');
  }

  if (e.server.nos.length > 0) document.getElementById('skeleton-palco')?.remove();

  /* primeira carga com dados: a porta de entrada e a ORGANIZACAO */
  if (!entradaOrganizacionalFeita && e.server.nos.length > 0) {
    entradaOrganizacionalFeita = true;
    setTimeout(() => {
      void entrarNaOrganizacao();
      if (ENTRADA_PADRAO === 'comando') abrirComando();
    }, 60);
  }

  /* NEXT-GEN: o header espelha o caminho (RMC › … › no) em uma linha com
     reticencias; a barra de contexto continua sendo o lugar de agir */
  const crumbs = document.getElementById('topo-crumbs');
  if (crumbs) {
    crumbs.textContent = '';
    const selCrumb = e.ui.selecionado ? noPorId(e, e.ui.selecionado) : null;
    const partes: string[] = [e.server.organizacao?.name ?? 'RMC'];
    let cursor: FomNode | null | undefined = selCrumb;
    const cadeiaTopo: string[] = [];
    let guardaTopo = 0;
    while (cursor && guardaTopo++ < 32) {
      cadeiaTopo.unshift(cursor.name);
      cursor = cursor.parent_external_id ? noPorId(e, cursor.parent_external_id) : null;
    }
    for (const nome of [...partes, ...cadeiaTopo]) {
      if (crumbs.childElementCount) {
        const sep = document.createElement('i');
        sep.textContent = '›';
        crumbs.appendChild(sep);
      }
      const s = document.createElement('span');
      s.textContent = nome;
      s.title = nome;
      crumbs.appendChild(s);
    }
    crumbs.lastElementChild?.classList.add('topo-crumb-atual');
  }

  /* breadcrumb: o caminho ate a raiz, clicavel — "sei onde estou e volto" */
  const pao = document.getElementById('pao');
  if (pao) {
    pao.textContent = '';
    const sel = e.ui.selecionado ? noPorId(e, e.ui.selecionado) : null;
    if (sel) {
      /* contexto comeca na ORGANIZACAO — dado real do banco (nao um no):
         "RMC › Departamento Internacional › …" */
      if (e.server.organizacao?.name) {
        const orgCrumb = document.createElement('button');
        orgCrumb.className = 'pao-item pao-org';
        orgCrumb.textContent = e.server.organizacao.name;
        orgCrumb.title = 'organizacao — enquadrar o mapa inteiro';
        orgCrumb.addEventListener('click', () => {
          store.selecionar(null);
          mapa?.apresentar();
        });
        pao.appendChild(orgCrumb);
        const sep0 = document.createElement('span');
        sep0.className = 'pao-sep';
        sep0.textContent = '›';
        pao.appendChild(sep0);
      }
      if (pilhaSelecao.length) {
        const volta = document.createElement('button');
        volta.className = 'pao-item pao-voltar';
        volta.textContent = '‹ voltar';
        volta.title = 'voltar ao no anterior';
        volta.addEventListener('click', voltarSelecao);
        pao.appendChild(volta);
      }
      const cadeia: FomNode[] = [];
      let atual: FomNode | null | undefined = sel;
      let guarda = 0;
      while (atual && guarda++ < 32) {
        cadeia.unshift(atual);
        atual = atual.parent_external_id ? noPorId(e, atual.parent_external_id) : null;
      }
      cadeia.forEach((n, i) => {
        if (i > 0) {
          const sep = document.createElement('span');
          sep.className = 'pao-sep';
          sep.textContent = '›';
          pao.appendChild(sep);
        }
        const b = document.createElement('button');
        b.className = 'pao-item' + (i === cadeia.length - 1 ? ' pao-atual' : '');
        b.textContent = n.name.length > 26 ? n.name.slice(0, 26) + '…' : n.name;
        b.title = n.name;
        b.dataset.previaId = n.external_id;
        b.addEventListener('click', () => irParaNo(n.external_id));
        pao.appendChild(b);
      });
    }
  }

  const selo = document.getElementById('selo-base');
  if (selo && e.server.fingerprint) {
    const ok = e.server.fingerprint.base_only_intact !== false;
    selo.classList.toggle('selo-base-alerta', !ok);
    selo.innerHTML = ok
      ? '<span class="selo-base-ponto"></span> BASE intacta'
      : '<span class="selo-base-ponto"></span> BASE ALTERADA';
    const det = document.getElementById('pulse-detalhe');
    if (det) {
      det.textContent = 'fingerprint ' + e.server.fingerprint.fingerprint_base_only.slice(0, 16)
        + '… recalculada do payload congelado · ' + (ok ? 'identica a homologada' : 'DIVERGENTE — investigar');
    }
  }

  /* (bloco #metricas removido na 1X.1 — o elemento nao existe desde a 1S.1
     e os numeros do cockpit agora derivam da visao, em contagensVisao) */

  /* nas lentes de futuro o layout compacta: fantasmas nao empurram parede */
  /* 1AE.1: perspectiva ORGANIZACAO — subarvores de processo saem do LAYOUT
     (filtro de apresentacao, como a lente FUTURA; aviso explicito na barra) */
  /* projecao organizacional: processos fora + participantes por relacao de
     suporte promovidos a IRMAOS espaciais da unit (copias; dado intocado) */
  const aposPerspectiva = perspectivaAtual === 'organizacao'
    ? projetarPerspectivaOrganizacao(e.server.nos, e.server.relacoes).visiveis
    : e.server.nos;
  const nosParaMapa = (lenteAtual === 'futura' || lenteAtual === 'mudancas')
    ? apenasFuturoEAncestrais(aposPerspectiva)
    : aposPerspectiva;
  mapa?.render({
    nos: nosParaMapa,
    relacoes: e.server.relacoes,
    selecionado: e.ui.selecionado,
    ligando: e.ui.ligando,
    relacaoSelecionada: e.ui.relacaoSelecionada,
    layout: {
      ajustes: e.server.posicoes,
      expandidos: e.ui.expandidos,
      filtroSt: e.ui.filtroSt,
      mostrarBase: e.ui.mostrarBase,
      mostrarUser: e.ui.mostrarUser,
    },
  });

  /* cabecalho/cartoes/titulo do workspace leem o layout ja calculado */
  pintarWorkspace();

  const selecionado = e.ui.selecionado ? noPorId(e, e.ui.selecionado) ?? null : null;
  const relSelecionada = e.ui.relacaoSelecionada
    ? e.server.relacoes.find((r) => r.id === e.ui.relacaoSelecionada) ?? null : null;
  painel?.render(selecionado, e.server.relacoes, e.server.nos, relSelecionada);

  /* A gaveta segue a selecao, mas so quando ja esta aberta: abrir sozinha a
     cada clique no mapa seria roubar a tela de quem so queria navegar. */
  if (selecionado?.external_id !== ultimoSelecionado) {
    ultimoSelecionado = selecionado?.external_id ?? null;
    void gaveta?.acompanhar(selecionado);
  }

  const aviso = document.getElementById('aviso');
  if (aviso) {
    if (e.ui.mensagem) {
      aviso.textContent = e.ui.mensagem.texto;
      aviso.className = 'aviso aviso-' + e.ui.mensagem.tipo;
      aviso.hidden = false;
      if (avisoTimer) clearTimeout(avisoTimer);
      avisoTimer = setTimeout(() => store.limparMensagem(), 6000);
    } else {
      aviso.hidden = true;
    }
  }

  const rodape = document.getElementById('rodape');
  if (rodape) {
    const h = e.server.historico[0];
    rodape.textContent = h
      ? `ultimo: ${h.action} · ${h.at.slice(0, 19).replace('T', ' ')}`
      : e.server.carregadoEm
        ? `carregado do banco em ${e.server.carregadoEm.slice(11, 19)}`
        : '';
  }

  document.body.classList.toggle('ocupado', e.ui.ocupado);
  document.body.classList.toggle('ligando', e.ui.ligando !== null);
}

/* ------------------------------------------------------------------- dados -- */
async function carregarMapa(recarregando = false): Promise<void> {
  store.ocupado(true);
  try {
    const orgs = await api.organizacoes();
    if (orgs.length === 0) {
      store.mensagem('erro', 'Este usuario nao tem vinculo com nenhuma organizacao.');
      return;
    }
    const org = orgs[0]!;
    orgSlug = org.slug;

    const { maps } = await api.mapas(orgSlug);
    if (maps.length === 0) {
      store.mensagem('erro', 'A organizacao nao tem mapa.');
      return;
    }
    mapSlug = maps[0]!.slug;

    /* Posicoes: FALHA de leitura != "nao ha posicoes". Um GET que falha
       devolve `null` e o store PRESERVA o que ja conhecia (definirMapa trata
       ausencia como "nao me falaram", nunca como "zere"); um GET vazio
       legitimo devolve {} e zera de fato. Antes, a falha virava {} e apagava
       os ajustes em memoria em silencio — o mapa mudava sem o dado mudar
       (FOM-ARQ-0049). */
    const [detalhe, nos, relacoes, fingerprint, historico, posicoes] = await Promise.all([
      api.mapa(orgSlug, mapSlug),
      api.nos(orgSlug, mapSlug),
      api.relacoes(orgSlug, mapSlug),
      api.fingerprint(orgSlug, mapSlug).catch(() => null),
      api.historico(orgSlug, mapSlug, 10).catch(() => []),
      api.posicoes(orgSlug, mapSlug).catch(() => null),
    ]);

    store.definirMapa({
      organizacao: org,
      mapa: detalhe.map,
      contagens: detalhe.counts,
      nos,
      relacoes,
      fingerprint,
      historico,
      ...(posicoes ? { posicoes } : {}),
    });

    if (!recarregando) {
      /* abre o primeiro nivel para o mapa nao aparecer como um ponto so */
      const raizNo = nos.find((n) => !n.parent_external_id);
      if (raizNo) {
        const primeiros = nos.filter((n) => n.parent_external_id === raizNo.external_id);
        const expandidos = new Set([raizNo.external_id, ...primeiros.map((n) => n.external_id)]);
        store.patchUi({ expandidos });
      }
    }
    /* O PAPEL vem do servidor, nunca da tela. A gaveta usa isso apenas para
       explicar o que o servidor ja recusaria — nao para autorizar nada. */
    papelAtual = org.role;
    if (!gaveta) {
      gaveta = montarGaveta(document.getElementById('gaveta')!, {
        api, org: orgSlug, mapa: mapSlug, papel: org.role,
        aoAlterarNo: async () => {
          const frescos = await api.nos(orgSlug, mapSlug);
          for (const n of frescos) store.aplicarNo(n);
          await atualizarContexto();
        },
        aoErro: (err) => tratarErro(err, 'operar a camada'),
      });
    }

    pintarUsuario(org.role);
    if (!usuarioNome) {
      void api.me().then((u) => {
        if (u.email) { usuarioNome = u.email.split('@')[0] ?? u.email; pintarUsuario(org.role); }
      }).catch(() => undefined);
    }
    void carregarKpis();
    void carregarTiposDeNo();
    void carregarGuiaOperacional();

    requestAnimationFrame(() => mapa?.apresentar());
    if (recarregando) store.mensagem('ok', `Recarregado do banco: ${nos.length} nos.`);
    if (!posicoes) {
      store.mensagem('aviso', 'Nao foi possivel ler as posicoes salvas; o mapa mantem as ultimas conhecidas.');
    }
  } catch (err) {
    store.mensagem('erro', 'Falha ao carregar o mapa: ' + (err as Error).message);
  } finally {
    store.ocupado(false);
  }
}

async function atualizarContexto(): Promise<void> {
  const [detalhe, fingerprint, historico] = await Promise.all([
    api.mapa(orgSlug, mapSlug),
    api.fingerprint(orgSlug, mapSlug).catch(() => null),
    api.historico(orgSlug, mapSlug, 10).catch(() => []),
  ]);
  const e = store.estado;
  store.definirMapa({
    organizacao: e.server.organizacao!,
    mapa: detalhe.map,
    contagens: detalhe.counts,
    nos: e.server.nos,
    relacoes: e.server.relacoes,
    fingerprint,
    historico,
  });
}

/* ---------------------------------------------------------------- mutacoes -- */
async function criarNo(pai: FomNode, dados: DadosNo): Promise<void> {
  store.ocupado(true);
  try {
    const novo = await api.criarNo(orgSlug, mapSlug, {
      parent_external_id: pai.external_id,
      name: dados.name,
      st: dados.st,
      ...(dados.node_type ? { node_type: dados.node_type } : {}),
      ...(dados.summary ? { summary: dados.summary } : {}),
      ...(dados.src ? { src: dados.src } : {}),
    });
    store.aplicarNo(novo);
    store.patchUi({ expandidos: new Set([...store.estado.ui.expandidos, pai.external_id]) });
    store.selecionar(novo.external_id);
    store.mensagem('ok', `No "${novo.name}" criado e persistido.`);
    histLocal.registrar({
      rotulo: `criar "${novo.name}"`,
      desfazer: async () => {
        await api.removerNo(orgSlug, mapSlug, novo.external_id);
        store.removerNo(novo.external_id);
        await atualizarContexto();
      },
      refazer: async () => {
        const volta = await api.restaurarNo(orgSlug, mapSlug, novo.external_id, 'visibility');
        store.aplicarNo(volta);
        await atualizarContexto();
      },
    });
    await atualizarContexto();
  } catch (err) {
    tratarErro(err, 'criar no');
  } finally {
    store.ocupado(false);
  }
}

async function editarNo(no: FomNode, dados: DadosNo): Promise<void> {
  store.ocupado(true);
  try {
    const antes = {
      name: no.name, st: no.st,
      node_type: no.node_type, summary: no.summary ?? '', src: no.src ?? '',
    };
    const atualizado = await api.editarNo(orgSlug, mapSlug, no.external_id, {
      expected_updated_at: no.updated_at,
      name: dados.name,
      st: dados.st,
      ...(dados.node_type ? { node_type: dados.node_type } : {}),
      ...(dados.summary !== undefined ? { summary: dados.summary } : {}),
      ...(dados.src !== undefined ? { src: dados.src } : {}),
    });
    store.aplicarNo(atualizado);
    store.mensagem('ok', 'Alteracao persistida.');
    /* cada direcao lembra o updated_at que ELA produziu: se o servidor mudar
       no meio, o proximo passo conflita (409) em vez de atropelar */
    const caixa = { doDesfazer: atualizado.updated_at, doRefazer: '' };
    const depois = {
      name: atualizado.name, st: atualizado.st,
      node_type: atualizado.node_type, summary: atualizado.summary ?? '', src: atualizado.src ?? '',
    };
    histLocal.registrar({
      rotulo: `editar "${antes.name}"`,
      desfazer: async () => {
        const n = await api.editarNo(orgSlug, mapSlug, no.external_id, {
          expected_updated_at: caixa.doDesfazer,
          name: antes.name, st: antes.st,
          ...(antes.node_type ? { node_type: antes.node_type } : {}),
          summary: antes.summary, src: antes.src,
        });
        caixa.doRefazer = n.updated_at;
        store.aplicarNo(n);
        await atualizarContexto();
      },
      refazer: async () => {
        const n = await api.editarNo(orgSlug, mapSlug, no.external_id, {
          expected_updated_at: caixa.doRefazer,
          name: depois.name, st: depois.st,
          ...(depois.node_type ? { node_type: depois.node_type } : {}),
          summary: depois.summary, src: depois.src,
        });
        caixa.doDesfazer = n.updated_at;
        store.aplicarNo(n);
        await atualizarContexto();
      },
    });
    await atualizarContexto();
  } catch (err) {
    tratarErro(err, 'editar no');
  } finally {
    store.ocupado(false);
  }
}

async function removerNo(no: FomNode): Promise<void> {
  store.ocupado(true);
  try {
    await api.removerNo(orgSlug, mapSlug, no.external_id);
    store.removerNo(no.external_id);
    store.mensagem('ok', `"${no.name}" saiu do mapa. O historico mantem o registro.`);
    histLocal.registrar({
      rotulo: `ocultar "${no.name}"`,
      desfazer: async () => {
        const volta = await api.restaurarNo(orgSlug, mapSlug, no.external_id, 'visibility');
        store.aplicarNo(volta);
        await atualizarContexto();
      },
      refazer: async () => {
        await api.removerNo(orgSlug, mapSlug, no.external_id);
        store.removerNo(no.external_id);
        await atualizarContexto();
      },
    });
    await atualizarContexto();
  } catch (err) {
    tratarErro(err, 'remover no');
  } finally {
    store.ocupado(false);
  }
}

/* Arrastar um no e escrita como qualquer outra: passa pela rota real e o
   banco e quem guarda. O eco local existe para a tela nao piscar, mas quem
   manda continua sendo o GET. */
async function moverNo(externalId: string, dx: number, dy: number): Promise<void> {
  const anterior = store.estado.server.posicoes[externalId] ?? { x: 0, y: 0 };
  const novo = { x: anterior.x + dx, y: anterior.y + dy };
  store.aplicarPosicao(externalId, novo.x, novo.y);
  try {
    await api.salvarPosicoes(orgSlug, mapSlug, [
      { node_ref: externalId, x: novo.x, y: novo.y },
    ]);
    histLocal.registrar({
      rotulo: 'mover no',
      desfazer: async () => {
        await api.salvarPosicoes(orgSlug, mapSlug, [
          { node_ref: externalId, x: anterior.x, y: anterior.y }]);
        store.aplicarPosicao(externalId, anterior.x, anterior.y);
      },
      refazer: async () => {
        await api.salvarPosicoes(orgSlug, mapSlug, [
          { node_ref: externalId, x: novo.x, y: novo.y }]);
        store.aplicarPosicao(externalId, novo.x, novo.y);
      },
    });
  } catch (err) {
    /* nao ficou salvo: desfaz o eco em vez de mentir que ficou */
    store.aplicarPosicao(externalId, anterior.x, anterior.y);
    tratarErro(err, 'mover o no');
  }
}

async function conectar(de: string, para: string): Promise<void> {
  const dialogo = document.getElementById('dialogo')!;
  const noDe = noPorId(store.estado, de);
  const noPara = noPorId(store.estado, para);
  if (!noDe || !noPara) return;

  store.patchUi({ ligando: null });
  formularioRelacao(dialogo, noDe, noPara, (dados) => {
    void (async () => {
      store.ocupado(true);
      try {
        const rel = await api.criarRelacao(orgSlug, mapSlug, {
          from: de, to: para, type: dados.type, kind: dados.kind,
        });
        store.aplicarRelacao(rel);
        store.mensagem('ok', `Conexao "${dados.type}" criada.`);
        const caixaRel = { id: rel.id };
        histLocal.registrar({
          rotulo: `conectar "${dados.type}"`,
          desfazer: async () => {
            await api.removerRelacao(orgSlug, mapSlug, caixaRel.id);
            store.removerRelacao(caixaRel.id);
            await atualizarContexto();
          },
          refazer: async () => {
            const outra = await api.criarRelacao(orgSlug, mapSlug, {
              from: de, to: para, type: dados.type, kind: dados.kind,
            });
            caixaRel.id = outra.id;
            store.aplicarRelacao(outra);
            await atualizarContexto();
          },
        });
        await atualizarContexto();
      } catch (err) {
        tratarErro(err, 'criar relacao');
      } finally {
        store.ocupado(false);
      }
    })();
  });
}

/* GATE 0062 · editar uma relacao existente: tipo e payload, os UNICOS campos
   que o PATCH do backend aceita. Retarget (from/to) nao existe por decisao de
   dominio — o formulario avisa e o caminho oficial e remover + "Conectar a…".
   O store so muda DEPOIS do 200: erro nunca deixa a tela mentindo. */
function editarRelacao(rel: Relation): void {
  const dialogo = document.getElementById('dialogo')!;
  const noDe = noPorId(store.estado, rel.from);
  const noPara = noPorId(store.estado, rel.to);
  if (!noDe || !noPara) {
    store.mensagem('erro', 'Nao encontrei as pontas desta conexao no mapa carregado. Recarregue e tente de novo.');
    return;
  }
  const antes = { type: rel.type, payload: rel.payload ?? '' };
  formularioRelacao(dialogo, noDe, noPara, (dados) => {
    const corpo: { type?: string; payload?: string } = {};
    if (dados.type !== antes.type) corpo.type = dados.type;
    if (dados.payload !== undefined && dados.payload !== antes.payload) corpo.payload = dados.payload;
    if (corpo.type === undefined && corpo.payload === undefined) {
      store.mensagem('aviso', 'Nada foi alterado.');
      return;
    }
    void (async () => {
      store.ocupado(true);
      try {
        const atualizada = await api.editarRelacao(orgSlug, mapSlug, rel.id, corpo);
        store.aplicarRelacao(atualizada);
        store.mensagem('ok', corpo.type !== undefined
          ? `Conexao atualizada: "${antes.type}" -> "${atualizada.type}".`
          : `Payload da conexao "${antes.type}" atualizado.`);
        const antesCorpo: { type?: string; payload?: string } = {};
        if (corpo.type !== undefined) antesCorpo.type = antes.type;
        if (corpo.payload !== undefined) antesCorpo.payload = antes.payload;
        const depoisCorpo = { ...corpo };
        histLocal.registrar({
          rotulo: `editar conexao "${antes.type}"`,
          desfazer: async () => {
            const r = await api.editarRelacao(orgSlug, mapSlug, rel.id, antesCorpo);
            store.aplicarRelacao(r);
            await atualizarContexto();
          },
          refazer: async () => {
            const r = await api.editarRelacao(orgSlug, mapSlug, rel.id, depoisCorpo);
            store.aplicarRelacao(r);
            await atualizarContexto();
          },
        });
        await atualizarContexto();
      } catch (err) {
        tratarErro(err, 'editar a conexao');
      } finally {
        store.ocupado(false);
      }
    })();
  }, { type: rel.type, kind: rel.kind, payload: rel.payload });
}

/* GATE 0062 · remover uma relacao: confirmacao explicita, DELETE existente
   (soft-delete, I-2), remocao da tela SO apos o 204, undo recria pela API. */
function confirmarRemoverRelacao(rel: Relation): void {
  const d = document.getElementById('dialogo')!;
  const noDe = noPorId(store.estado, rel.from);
  const noPara = noPorId(store.estado, rel.to);
  d.innerHTML = '';
  d.hidden = false;
  const box = document.createElement('div');
  box.className = 'dialogo';
  box.innerHTML = `
    <h3>Remover conexao</h3>
    <p class="dialogo-sub"><b></b> → <b></b></p>
    <p class="ajuda">A conexao <b class="rel-remover-tipo"></b> sera removida do mapa
      (remocao logica — a trilha mantem o registro). A hierarquia pai/filho nao muda.</p>
    <div class="dialogo-acoes">
      <button type="button" class="botao" data-cancelar>Cancelar</button>
      <button type="button" class="botao botao-perigo" data-confirmar>Remover</button>
    </div>`;
  const negritos = box.querySelectorAll<HTMLElement>('.dialogo-sub b');
  negritos[0]!.textContent = noDe?.name ?? rel.from;
  negritos[1]!.textContent = noPara?.name ?? rel.to;
  box.querySelector('.rel-remover-tipo')!.textContent = `"${rel.type}"`;
  const fechar = (): void => { d.hidden = true; d.innerHTML = ''; };
  box.querySelector('[data-cancelar]')!.addEventListener('click', fechar);
  box.querySelector('[data-confirmar]')!.addEventListener('click', () => {
    fechar();
    void (async () => {
      store.ocupado(true);
      try {
        await api.removerRelacao(orgSlug, mapSlug, rel.id);
        /* so depois do 204 — falha nao pode sumir com a linha da tela */
        store.removerRelacao(rel.id);
        store.mensagem('ok', `Conexao "${rel.type}" removida. A trilha guarda o registro.`);
        const caixa = { id: rel.id };
        histLocal.registrar({
          rotulo: `remover conexao "${rel.type}"`,
          desfazer: async () => {
            const volta = await api.criarRelacao(orgSlug, mapSlug, {
              from: rel.from, to: rel.to, type: rel.type, kind: rel.kind,
              ...(rel.payload ? { payload: rel.payload } : {}),
            });
            caixa.id = volta.id;
            store.aplicarRelacao(volta);
            await atualizarContexto();
          },
          refazer: async () => {
            await api.removerRelacao(orgSlug, mapSlug, caixa.id);
            store.removerRelacao(caixa.id);
            await atualizarContexto();
          },
        });
        await atualizarContexto();
      } catch (err) {
        tratarErro(err, 'remover a conexao');
      } finally {
        store.ocupado(false);
      }
    })();
  });
  d.appendChild(box);
}

/* 409 nunca vira sobrescrita: recarrega o recurso do servidor e preserva a
   versao dele. Quem decide o que fazer com a propria edicao e a pessoa. */
function tratarErro(err: unknown, acao: string): void {
  if (err instanceof ConflitoError) {
    store.mensagem('erro',
      'Alguem alterou este no enquanto voce editava. A versao do servidor foi mantida; recarregue e refaca.');
    void carregarMapa(true);
    return;
  }
  if (err instanceof ApiError) {
    if (err.status === 403) {
      store.mensagem('erro', 'Seu papel nesta organizacao nao permite esta acao.');
      return;
    }
    if (err.status === 422) {
      store.mensagem('erro', 'Recusado por regra de dominio: ' + err.message);
      return;
    }
    store.mensagem('erro', `Falha ao ${acao}: ${err.message}`);
    return;
  }
  store.mensagem('erro', `Falha ao ${acao}: ${(err as Error).message}`);
}

/* ------------------------------------------------------------------ sessao -- */
async function entrar(email: string, senha: string): Promise<void> {
  const s = await api.login(email, senha);
  /* o refresh token esta no cookie HttpOnly; daqui so levamos o NOME, que e
     dado de exibicao, nao credencial */
  usuarioNome = s.user?.name ?? '';
  montarAplicacao();
  await carregarMapa();
}

async function sair(): Promise<void> {
  gaveta = null;
  /* 0075A: o guia e conteudo de UMA organizacao. Sair limpa, para que a
     proxima sessao nunca herde o guia da anterior. */
  definirGuiaOperacional(null);
  await api.sair();
  telaLogin();
}

function telaLogin(primeiroAcesso: boolean = false): void {
  montarTelaLogin(raiz, {
    aoEntrar: entrar,
    dica: import.meta.env?.DEV
      ? { email: 'dev@fom.local', nota: 'Ambiente de desenvolvimento: use o usuario tecnico do seed.' }
      : null,
    primeiroAcesso: primeiroAcesso
      ? {
          minimoSenha: 12,
          aoCriar: (nome, email, senha, confirmacao) =>
            api.criarPrimeiroAcesso(nome, email, senha, confirmacao),
        }
      : null,
  });
}

/* Pergunta ao servidor e so entao remonta a tela. Enquanto a resposta nao chega,
   a tela de login normal ja esta no ar — quem tem conta nao espera por quem nao
   tem. */
function telaLoginComPrimeiroAcesso(): void {
  telaLogin(false);
  void api.bootstrapDisponivel().then((disponivel) => {
    if (disponivel) telaLogin(true);
  });
}

async function iniciar(): Promise<void> {
  api.aoExpirar(() => { telaLogin(); });

  /* Retomar sessao sem ter guardado nada: pede renovacao e deixa o navegador
     anexar o cookie. Se nao houver sessao valida, cai na tela de login — que e
     o comportamento correto, e nao um erro. */
  try {
    await api.retomar();
    montarAplicacao();
    await carregarMapa();
    return;
  } catch {
    /* sem sessao: segue para o login */
  }
  telaLoginComPrimeiroAcesso();
}

void iniciar();
